import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import {
  getFirestore,
  doc,
  getDoc,
  getDocs,
  collection,
} from "firebase/firestore";
import { useParams } from "src/hooks/useParams";
import {
  videoChooseProps,
  nextActionsType,
  videoType,
  videosType,
  IVideoChooseForm,
  FormFields,
} from "src/types/video";
import LinearProgress from "@mui/material/LinearProgress";
import TouchAppIcon from "@mui/icons-material/TouchApp";
import {
  Box,
  Button,
  CircularProgress,
  Typography,
  Dialog,
  DialogTitle,
  DialogActions,
  Stack,
} from "@mui/material";
import Iconify from "src/components/Iconify";
import { useSnackbar } from "notistack";
import { useResponsive } from "src/hooks/useResponsive";
import logRequest from "src/api/logs";
import uuidv4 from "src/utils/uuidv4";
import Modal from "@mui/material/Modal";
import VideoChooseForm from "./VideoChooseComponent/VideoChooseForm";
import Fade from "@mui/material/Fade";
import { Swiper, SwiperSlide } from "swiper/react";
import { Swiper as SwiperCore } from "swiper/types";
import "swiper/css";

interface MessageData {
  action:
    | "play"
    | "pause"
    | "openIFrame"
    | "openForm"
    | "closeForm"
    | "loading"
    | "withdrawal_play";
  form_id?: string;
}

const sendMessage = (
  anchor: string | undefined,
  is_close_anchor_click: boolean | undefined,
) => {
  window.parent.postMessage(
    {
      action: "navigate",
      anchor: `#${anchor}`,
      close: is_close_anchor_click,
    },
    "*",
  );
};

const sendCostomScriptIdMessage = (
  scriptId: string | undefined,
  videoChooseId: string | undefined,
) => {
  window.parent.postMessage(
    {
      action: "customScript",
      scriptId: scriptId,
      videoChooseId: videoChooseId,
    },
    "*",
  );
};

type SubtitleCue = { start: number; end: number; text: string };

interface SubtitleProps {
  subtitleUrl: string | null;
  subtitles: SubtitleCue[];
  currentTime: number;
  inline?: boolean;
  subtitleTextColor: string | null;
  subtitleBackgroundColor: string | null;
  subtitleFontSize: number;
  subtitleFontFamily?: string | null;
  subtitleBold?: boolean | null;
  onClick?: (e: React.MouseEvent) => void;
}

interface AnnotationProps {
  annotationUrl: string | null;
  currentTime: number;
  annotationFontSize: number;
  annotationBackgroundBlur: boolean;
}

const parseTimestamp = (timestamp: string) => {
  const [hours, minutes, seconds] = timestamp.split(":").map(parseFloat);
  return hours * 3600 + minutes * 60 + seconds;
};

function getJSTDate() {
  const now = new Date();
  const utc = now.getTime() + now.getTimezoneOffset() * 60000;
  const jst = new Date(utc + 9 * 60 * 60 * 1000);

  const year = jst.getFullYear();
  const month = String(jst.getMonth() + 1).padStart(2, "0");
  const day = String(jst.getDate()).padStart(2, "0");
  const hours = String(jst.getHours()).padStart(2, "0");
  const minutes = String(jst.getMinutes()).padStart(2, "0");
  const seconds = String(jst.getSeconds()).padStart(2, "0");

  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}+09:00`;
}

// 開閉する字幕表示とは別にデータを保持し、URLが変わったときだけ取得する。
function useSubtitles(subtitleUrl: string | null): SubtitleCue[] {
  const [loaded, setLoaded] = useState<{
    url: string | null;
    cues: SubtitleCue[];
  }>({ url: null, cues: [] });

  useEffect(() => {
    if (!subtitleUrl) return;
    const controller = new AbortController();

    const fetchSubtitles = async () => {
      try {
        const response = await fetch(subtitleUrl, {
          signal: controller.signal,
        });
        if (!response.ok)
          throw new Error(`字幕の取得に失敗しました: ${response.status}`);
        const data = await response.json();
        if (!Array.isArray(data))
          throw new Error("字幕データが配列ではありません");
        const cues = data.map((item) => ({
          start: parseTimestamp(item.start),
          end: parseTimestamp(item.end),
          text: item.text,
        }));
        if (!controller.signal.aborted) setLoaded({ url: subtitleUrl, cues });
      } catch (error) {
        if (!controller.signal.aborted) {
          setLoaded({ url: subtitleUrl, cues: [] });
          console.error("字幕の読み込みに失敗しました", error);
        }
      }
    };

    fetchSubtitles();
    return () => controller.abort();
  }, [subtitleUrl]);

  return loaded.url === subtitleUrl ? loaded.cues : [];
}

const Subtitle: React.FC<SubtitleProps> = ({
  subtitleUrl,
  subtitles,
  currentTime,
  inline,
  subtitleTextColor,
  subtitleBackgroundColor,
  subtitleFontSize,
  subtitleFontFamily,
  subtitleBold,
  onClick,
}) => {
  useEffect(() => {
    const style = document.createElement("style");

    const fontFaceMap: Record<string, string> = {
      "Noto Sans JP": `
        @font-face {
          font-family: "Noto Sans JP";
          src: url("${process.env.PUBLIC_URL}/assets/fonts/NotoSansJP-Regular.ttf") format("truetype");
          font-weight: 400;
          font-style: normal;
        }
      `,
      Tsunagi: `
        @font-face {
          font-family: "Tsunagi";
          src: url("${process.env.PUBLIC_URL}/assets/fonts/TsunagiGothic.ttf") format("truetype");
          font-weight: 400;
          font-style: normal;
        }
      `,
    };

    const computedFamilyRaw = subtitleFontFamily || "Open Sans";
    const selectedFont = subtitleFontFamily
      ? fontFaceMap[subtitleFontFamily] || ""
      : "";

    style.innerHTML = `
      ${selectedFont}
      .video-choose-subtitle {
        font-family: ${computedFamilyRaw}, sans-serif !important;
      }
    `;

    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, [subtitleFontFamily]);

  const currentSubtitle = subtitles.find(
    (subtitle) => currentTime >= subtitle.start && currentTime <= subtitle.end,
  );

  const renderStyledText = (text: string) => {
    // *数字のパターンと※数字のパターンの両方を処理
    const parts = text.split(/(\*\d+|\※\d+)/);
    return parts.map((part, index) => {
      if (
        (part.startsWith("*") && /^\*\d+$/.test(part)) ||
        (part.startsWith("※") && /^\※\d+$/.test(part))
      ) {
        return (
          <span
            key={index}
            style={{ fontSize: "0.7em", verticalAlign: "super" }}
          >
            {part}
          </span>
        );
      }
      return part;
    });
  };

  return (
    <div
      onClick={onClick}
      style={{
        position: inline ? "static" : "absolute",
        minWidth: "50%",
        textAlign: "center",
        zIndex: 350,
        width: "100%",
        maxWidth: 320,
        ...(inline
          ? { margin: "0.5rem auto 0.5rem" }
          : { bottom: "120px", left: "50%", transform: "translateX(-50%)" }),
        ...(subtitleFontSize >= 20 && {
          minHeight: 60,
          display: "flex",
          justifyContent: "center",
          alignItems: "flex-end",
        }),
      }}
    >
      {currentSubtitle ? (
        <div
          style={{
            backgroundColor: subtitleBackgroundColor || "rgba(20, 20, 20, 0.5)",
            color: subtitleTextColor || "white",
            padding: "5px 10px",
            borderRadius: "5px",
            fontSize: subtitleFontSize,
            fontWeight: subtitleBold ? 700 : 400,
            minHeight: 30,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            maxWidth: 320,
            margin: "0 auto",
            ...(subtitleFontSize >= 20 && {
              width: "100%",
            }),
          }}
        >
          <span className="video-choose-subtitle">
            {/* {currentSubtitle.text} */}
            {renderStyledText(currentSubtitle.text)}
          </span>
        </div>
      ) : (
        subtitleUrl && (
          <div
            style={{
              backgroundColor: "transparent",
              padding: "5px 10px",
              minHeight: 30,
              maxWidth: 320,
              margin: "0 auto",
            }}
          ></div>
        )
      )}
    </div>
  );
};

const Annotation: React.FC<AnnotationProps> = ({
  annotationUrl,
  currentTime,
  annotationFontSize,
  annotationBackgroundBlur,
}) => {
  const [annotations, setAnnotations] = useState<
    { start: number; end: number; text: string }[]
  >([]);

  useEffect(() => {
    const fetchAnnotations = async () => {
      if (annotationUrl) {
        const response = await fetch(annotationUrl);
        const data = await response.json();

        // データが配列であることを確認
        if (Array.isArray(data)) {
          // 配列の各要素が連想配列であることを確認
          const isAssociativeArray = data.every(
            (item) => typeof item === "object" && item !== null,
          );

          if (isAssociativeArray) {
            // データを適切な型に変換
            const parsedAnnotations = data.map((item) => ({
              start: parseTimestamp(item.start),
              end: parseTimestamp(item.end),
              text: item.text,
            }));

            setAnnotations(parsedAnnotations);
          } else {
            console.log(
              "The elements of the data array are not associative arrays",
            );
          }
        } else {
          console.log("The data is not an array");
        }
      } else {
        setAnnotations([]);
      }
    };

    fetchAnnotations();
  }, [annotationUrl]);

  const currentSubtitle = annotations.find(
    (annotation) =>
      currentTime >= annotation.start && currentTime <= annotation.end,
  );

  return (
    <>
      {currentSubtitle ? (
        <div
          style={{
            color: "#000",
            padding: "5px 10px",
            borderRadius: "5px",
            fontSize: annotationFontSize,
            minHeight: 15,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            maxWidth: 320,
            margin: "0 auto",
            ...(annotationBackgroundBlur && {
              backdropFilter: "blur(2px)",
              WebkitBackdropFilter: "blur(2px)",
              backgroundColor: "rgba(255, 255, 255, 0.4)",
            }),
          }}
        >
          <span
            style={{
              color: "black",
              textShadow: "0px 0px 0.5px white",
            }}
          >
            {currentSubtitle.text}
          </span>
        </div>
      ) : (
        annotationUrl && (
          <div
            style={{
              backgroundColor: "transparent",
              padding: "5px 10px",
              minHeight: 30,
              maxWidth: 320,
              margin: "0 auto",
            }}
          ></div>
        )
      )}
    </>
  );
};

export default function VideoChooseV2() {
  //動画タグのref
  const videoRef = useRef<HTMLVideoElement>(null);
  const mdUp = useResponsive("up", "md");
  const iframeXSdown = useResponsive("down", "iframeXS");

  //選択肢のボックスのref
  const boxRef = useRef<HTMLDivElement>(null);

  //URLからvideoChooseIdを取得
  const params = useParams();
  const { videoChooseId } = params;
  const searchParams = new URLSearchParams(window.location.search);
  const isIFrame = searchParams.get("iframe") === "true";
  const hasLandingHub = searchParams.get("lh") === "true";
  const isClose = searchParams.get("close") === "true";
  const isFirstAutoPlay = searchParams.get("autoplay") === "true";
  const vcjsVersion = searchParams.get("vcjs_version") || "";
  const [firstAutoPlay, setFirstAutoPlay] = useState<boolean>(isFirstAutoPlay);

  //videoChooseインスタンス
  const [videoChoose, setVideoChoose] = useState<videoChooseProps>({
    videos: {},
    nextActions: {},
    videosOrdered: [],
    formGroups: {},
    formType: "default",
    displaySettings: {},
  });
  const [opacity, setOpacity] = useState(1); // 透明度の状態

  //再生されるvideo
  const [video, setVideo] = useState<videoType | null>(null);
  const subtitles = useSubtitles(video?.subtitle_url || null);

  //ProgressBarの値
  const [progress, setProgress] = useState<number>(0);

  //プログレスバーのドラッグ状態
  const [isDraggingProgress, setIsDraggingProgress] = useState<boolean>(false);

  //動画の長さ
  const [videoDuration, setVideoDuration] = useState<number>(0);

  //ローディング状態
  const [loading, setLoading] = useState<boolean>(true);

  //Videoローディング状態
  const [videoLoading, setVideoLoading] = useState<boolean>(false);

  //iframe遷移用 setIframeLoading
  const [iframeLoading, setIframeLoading] = useState<boolean>(false);

  //選択肢のトグルボタンの表示
  const [isShowButton, setIsShowButton] = useState<boolean>(false);

  // 選択肢が下に続くかどうか
  const [hasMoreContent, setHasMoreContent] = useState<boolean>(false);
  const [hasScrolledOptions, setHasScrolledOptions] = useState(false);

  //選択ボタンの透明度
  const [buttonOpacity, setButtonOpacity] = useState(1);

  //再生ボタンの透明度
  const [playButtonOpacity, setPlayButtonOpacity] = useState(1);

  //動画の終了状況の確認
  const [isVideoEnd, setIsVideoEnd] = useState(false);

  //ProgressBarのアニメーション
  const [progressTransition, setProgressTransition] = useState("0.3s linear");

  //自動再生(制御に使用するのみ、最初の動画は再生せず、選択肢を押した後の動画は自動再生できるようにする)
  const [autoPlay, setAutoPlay] = useState<boolean>(false);

  //動画のミュート
  const [isMuted, setIsMuted] = useState<boolean>(
    isFirstAutoPlay ? true : false,
  );

  const [closeButtonColor, setCloseButtonColor] = useState<string>(
    "rgba(255,255,255, 1)",
  );

  const [closeButtonPosition, setCloseButtonPosition] =
    useState<string>("top-right");

  //動画の速度
  const [playbackRate, setPlaybackRate] = useState<number>(1);

  //動画再生履歴
  const [history, setHistory] = useState<string[]>([]);

  //共有リンク
  const [shareLink, setShareLink] = useState<boolean>(false);
  const [changeSpeedButton, setChangeSpeedButton] = useState<boolean>(true);
  const [allowExpandButton, setAllowExpandButton] = useState<boolean>(false);
  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const [isParentMobile, setIsParentMobile] = useState<boolean>(true);
  const changeSpeed = (newRate: number) => {
    const videoEl = videoRef.current;
    if (!videoEl) return;

    const currentTime = videoEl.currentTime;
    videoEl.playbackRate = newRate;

    // 再生位置を固定
    videoEl.currentTime = currentTime + 0.15;
  };

  const [forms, setForms] = useState<IVideoChooseForm>({});

  // フォームの入力値を保存する
  const [savedValues, setSavedValues] = useState<{ [key: string]: any }>({});

  const [formModal, setFormModal] = useState<boolean>(false);
  const [customHtmlModal, setCustomHtmlModal] = useState<boolean>(false);
  const [selectedCustomHtml, setSelectedCustomHtml] = useState<string>("");
  const [selectedCustomHtmlActionId, setSelectedCustomHtmlActionId] =
    useState<string>("");
  const [customHtmlImageLoaded, setCustomHtmlImageLoaded] =
    useState<boolean>(false);
  const [selectedFormId, setSelectedFormId] = useState<string>("");
  const [selectedFormStep, setSelectedFormStep] = useState<number>(0);
  const [filledFormStep, setFilledFormStep] = useState<number>(0);
  const [playButtonClass, setPlayButtonClass] = useState(
    "video-choose-play-button",
  );
  const [audioPulseClass, setAudioPulseClass] = useState<string>("");

  //初回のみ再生ログを送信する
  const [playLogRequest, setPlayLogRequest] = useState<boolean>(false);

  //初回のみ訪問ログを送信する
  const [visitLogRequest, setVisitLogRequest] = useState<boolean>(false);

  const [vcReadyPostMessage, setVcReadyPostMessage] = useState<boolean>(false);

  // let autoCloseVideos: string[] = [];
  const [autoCloseVideos, setAutoCloseVideos] = useState<string[]>([]);

  const [hideCopyright, setHideCopyright] = useState<boolean>(false);

  const [confirmation, setConfirmation] = useState<boolean>(false);

  const [domain, setDomain] = useState<string | undefined | null>(undefined);
  const [changedUrl, setChangedUrl] = useState<string | undefined | null>(
    undefined,
  );
  const [fontType, setFontType] = useState<string>("Noto Sans JP");

  // 閉じる確認ダイアログの状態
  const [showCloseDialog, setShowCloseDialog] = useState<boolean>(false);

  // 閉じる確認ダイアログを表示するかどうかの設定
  const [isCloseDialogOpen, setIsCloseDialogOpen] = useState<boolean>(false);

  const handleChangeConfirmation = () => {
    setConfirmation((prev) => !prev);
  };

  // 閉じる確認ダイアログのハンドラー
  const handleCloseDialogOpen = () => {
    // 動画を停止
    if (videoRef.current) {
      videoRef.current.pause();

      // 停止イベントを親ページに送信
      window.parent.postMessage(
        {
          action: "vc_video_pause",
          videoId: video?.id,
          videoChooseId: videoChooseId,
          domain: domain,
          currentTime: videoRef.current?.currentTime || 0,
        },
        "*",
      );
    }
    setShowCloseDialog(true);
  };

  const handleCloseDialogClose = () => {
    setShowCloseDialog(false);
    // 動画を再開
    if (videoRef.current) {
      videoRef.current.play();

      // 再生イベントを親ページに送信
      window.parent.postMessage(
        {
          action: "vc_video_play",
          videoId: video?.id,
          videoChooseId: videoChooseId,
          domain: domain,
          currentTime: videoRef.current?.currentTime || 0,
        },
        "*",
      );
    }
  };

  const handleConfirmClose = () => {
    // 実際に閉じる処理を実行
    window.parent.postMessage(
      {
        action: "closeVideoChoose",
        videoChooseId: videoChooseId,
      },
      "*",
    );
    window.parent.postMessage(
      {
        action: "vc_close",
        videoId: video?.id,
        videoChooseId: videoChooseId,
        currentTime: videoRef.current?.currentTime || 0,
      },
      "*",
    );
    setShowCloseDialog(false);
  };

  const handleCancelClose = () => {
    // ダイアログを閉じて動画に戻る
    setShowCloseDialog(false);
    // 動画を再開
    if (videoRef.current) {
      videoRef.current.play();

      // 再生イベントを親ページに送信

      window.parent.postMessage(
        {
          action: "vc_video_play",
          videoId: video?.id,
          videoChooseId: videoChooseId,
          domain: domain,
          currentTime: videoRef.current?.currentTime || 0,
        },
        "*",
      );
    }
  };

  // 直接閉じる処理（ダイアログなし）
  const handleDirectClose = () => {
    window.parent.postMessage(
      {
        action: "closeVideoChoose",
        videoChooseId: videoChooseId,
      },
      "*",
    );
    window.parent.postMessage(
      {
        action: "vc_close",
        videoId: video?.id,
        videoChooseId: videoChooseId,
        currentTime: videoRef.current?.currentTime || 0,
      },
      "*",
    );
  };
  const swiperRef = useRef<SwiperCore | null>(null);

  const handleNext = () => {
    if (swiperRef.current) {
      swiperRef.current.slideNext();
    }
  };

  const handlePrev = () => {
    if (swiperRef.current) {
      swiperRef.current.slidePrev(); // 前のスライドに戻る
    }
  };

  const handleSlideChange = (type: string) => {
    if (type === "next") {
      handleNext();
    } else {
      handlePrev();
    }
  };

  let consumerToken = localStorage.getItem("consumerToken") || uuidv4();

  if (!localStorage.getItem("consumerToken")) {
    localStorage.setItem("consumerToken", consumerToken);
  }

  const visitToken = uuidv4();

  //通知バー
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    const handleTouchMove = (event: TouchEvent) => {
      if (customHtmlModal) {
        return;
      }
      const start = swipeGesture.current;
      if (event.touches.length !== 1) {
        swipeGesture.current = null;
      } else if (start) {
        const dx = event.touches[0].clientX - start.x;
        const dy = event.touches[0].clientY - start.y;
        if (Math.hypot(dx, dy) > 10)
          suppressClickUntil.current = Date.now() + 700;
        if (start.canClose && dy > 0 && Math.abs(dy) > Math.abs(dx)) {
          // 先頭から下への操作だけを閉じる操作にし、通常スクロールは維持する。
          if (event.cancelable) event.preventDefault();
          return;
        }
      }
      // event.targetがテキストエリアのrefであるかを確認
      if (boxRef.current && boxRef.current.contains(event.target as Node)) {
        // テキストエリア内でのイベントは通常通り処理（ここでは何もしない）
      } else {
        // テキストエリア外でのスクロールを防ぐ
        event.preventDefault();
      }
    };

    // touchmoveイベントでページ全体のスクロールを制御
    document.addEventListener("touchmove", handleTouchMove, { passive: false });

    // コンポーネントがアンマウントされる時にイベントリスナーをクリーンアップ
    return () => {
      document.removeEventListener("touchmove", handleTouchMove);
    };
  }, [customHtmlModal]);

  //選択肢を画面上に表示させる
  const handleScrollToTop = () => {
    setIsShowButton(false);
    setButtonOpacity(0);
  };

  //選択肢を画面下に隠す
  const handleScrollToBottom = () => {
    setIsShowButton(true);
    setButtonOpacity(1);
    setHasMoreContent(false);
  };

  const swipeGesture = useRef<{
    x: number;
    y: number;
    canClose: boolean;
    closed: boolean;
  } | null>(null);
  const suppressClickUntil = useRef(0);

  const beginSwipe = (x: number, y: number, onOptions: boolean) => {
    suppressClickUntil.current = 0;
    swipeGesture.current = {
      x,
      y,
      canClose: onOptions && (boxRef.current?.scrollTop || 0) <= 2,
      closed: isShowButton,
    };
  };
  const endSwipe = (x: number, y: number) => {
    const start = swipeGesture.current;
    swipeGesture.current = null;
    if (!start) return;
    const dx = x - start.x;
    const dy = y - start.y;
    if (Math.hypot(dx, dy) > 10) suppressClickUntil.current = Date.now() + 700;
    if (
      Math.abs(dy) <= 60 ||
      Math.abs(dy) <= Math.abs(dx) ||
      start.closed !== isShowButton
    )
      return;
    if (start.closed && dy < 0) handleScrollToTop();
    else if (start.canClose && dy > 0) handleScrollToBottom();
  };
  const suppressSwipeClick = (event: React.MouseEvent) => {
    if (Date.now() < suppressClickUntil.current) {
      event.preventDefault();
      event.stopPropagation();
    }
  };
  const swipeHandlers = (onOptions: boolean) => ({
    onTouchStart: (event: React.TouchEvent) => {
      if (event.touches.length !== 1) {
        swipeGesture.current = null;
        return;
      }
      beginSwipe(event.touches[0].clientX, event.touches[0].clientY, onOptions);
      if (onOptions) setHasScrolledOptions(true);
    },
    onTouchEnd: (event: React.TouchEvent) => {
      if (event.changedTouches.length)
        endSwipe(
          event.changedTouches[0].clientX,
          event.changedTouches[0].clientY,
        );
    },
    onTouchCancel: () => {
      swipeGesture.current = null;
    },
    onPointerDown: (event: React.PointerEvent) => {
      if (event.pointerType === "mouse" && event.button === 0)
        beginSwipe(event.clientX, event.clientY, onOptions);
    },
    onPointerUp: (event: React.PointerEvent) => {
      if (event.pointerType === "mouse") endSwipe(event.clientX, event.clientY);
    },
    onPointerCancel: (event: React.PointerEvent) => {
      if (event.pointerType === "mouse") swipeGesture.current = null;
    },
    onClickCapture: suppressSwipeClick,
  });

  // 最後の選択肢が半分未満しか見えていないときだけ案内する
  const updateOptionVisibility = useCallback((box: HTMLDivElement) => {
    const footer = box.lastElementChild;
    const lastOption = footer?.hasAttribute("data-vc-options-footer")
      ? footer.previousElementSibling
      : null;
    let isHidden = false;
    if (lastOption && box.scrollHeight > box.clientHeight + 2) {
      const boxRect = box.getBoundingClientRect();
      const optionRect = lastOption.getBoundingClientRect();
      const visibleHeight = Math.max(
        0,
        Math.min(optionRect.bottom, boxRect.bottom) -
          Math.max(optionRect.top, boxRect.top),
      );
      isHidden =
        optionRect.bottom > boxRect.bottom + 2 &&
        visibleHeight + 2 < optionRect.height / 2;
    }
    setHasMoreContent(isHidden);
  }, []);

  const checkScrollable = () => {
    if (boxRef.current && !isShowButton) updateOptionVisibility(boxRef.current);
  };

  //最初に動画の選択肢を表示させる
  useEffect(() => {
    if (video) {
      if (boxRef.current) boxRef.current.scrollTop = 0;
      setIsShowButton(false);
      setButtonOpacity(0);
      setHasScrolledOptions(false);
    }
  }, [video]);

  // ボタンや画像の描画、画面サイズの変化後にもスクロール量を更新する
  useLayoutEffect(() => {
    const box = boxRef.current;
    if (!box || isShowButton) {
      setHasMoreContent(false);
      return;
    }

    const update = () => {
      updateOptionVisibility(box);
    };
    update();

    const observer = new ResizeObserver(update);
    observer.observe(box);
    Array.from(box.children).forEach((child) => observer.observe(child));
    return () => observer.disconnect();
  }, [
    isShowButton,
    video,
    videoChoose,
    iframeXSdown,
    hideCopyright,
    updateOptionVisibility,
  ]);

  useEffect(() => {
    const checkTime = () => {
      if (
        videoRef.current &&
        video &&
        video.open_option_time &&
        videoRef.current.currentTime >= video.open_option_time
      ) {
        handleScrollToTop();
        videoRef.current.removeEventListener("timeupdate", checkTime);
      }
    };

    if (videoRef.current && video && video.open_option_time) {
      videoRef.current.addEventListener("timeupdate", checkTime);
    }

    return () => {
      if (videoRef.current && video && video.open_option_time) {
        videoRef.current.removeEventListener("timeupdate", checkTime);
      }
    };
  }, [video]);

  //progressの値を更新する関数
  //imageのカバーを透明にする
  const handleTimeUpdate = () => {
    const videoEl = videoRef.current;
    if (videoEl && videoEl.currentTime > 0) {
      setVideoLoading(false);
      setOpacity(0);
    }
    if (videoEl) {
      const currentProgress = (videoEl.currentTime / videoEl.duration) * 100;
      setProgress(currentProgress);
    }
  };

  const handleFormStep = (formIndex: number, formId: string) => {
    if (forms[formId].form_group) {
      setSelectedFormStep(formIndex);

      setSelectedFormId(
        // @ts-ignore: Suppress TypeScript error for indexing with potentially undefined key
        videoChoose.formGroups[forms[formId].form_group].formOrder[formIndex]
          .formId,
      );

      if (forms[formId].action_type === "form") {
        setFilledFormStep(formIndex);
      }
    }
  };

  const handleFormStepProgressBar = (formIndex: number, formId: string) => {
    if (forms[formId].form_group) {
      setSelectedFormStep(formIndex);

      setSelectedFormId(
        // @ts-ignore: Suppress TypeScript error for indexing with potentially undefined key
        videoChoose.formGroups[forms[formId].form_group].formOrder[formIndex]
          .formId,
      );
    }
  };

  //再生中にprogressの値を更新する関数を実行する
  useEffect(() => {
    const videoEl = videoRef.current;
    if (videoEl) {
      // メタデータ読み込み時にdurationを取得
      const handleLoadedMetadata = () => {
        if (
          videoEl.duration &&
          !isNaN(videoEl.duration) &&
          videoEl.duration !== Infinity
        ) {
          setVideoDuration(videoEl.duration);
        }
      };

      videoEl.addEventListener("timeupdate", handleTimeUpdate);
      videoEl.addEventListener("loadedmetadata", handleLoadedMetadata);

      // 既にメタデータが読み込まれている場合
      if (
        videoEl.duration &&
        !isNaN(videoEl.duration) &&
        videoEl.duration !== Infinity
      ) {
        setVideoDuration(videoEl.duration);
      }

      return () => {
        videoEl.removeEventListener("timeupdate", handleTimeUpdate);
        videoEl.removeEventListener("loadedmetadata", handleLoadedMetadata);
      };
    }
  }, [video, videoChoose]);

  /*
  動画が終了した時に、ProgressBarのアニメーションを停止する
  isVideoEndがtrueになると、巻き戻しボタンが表示される
  */
  useEffect(() => {
    const videoEl = videoRef.current;

    if (videoEl) {
      const handleVideoEnd = (): void => {
        setIsVideoEnd(true);
        setProgressTransition("none");
        if (video && video.is_auto_form_at_end) {
          if (video.form_id) {
            setSelectedFormId(video.form_id);

            if (forms[video.form_id] && forms[video.form_id].form_group) {
              const formStep = videoChoose.formGroups[
                // @ts-ignore: Suppress TypeScript error for indexing with potentially undefined key
                forms[video.form_id].form_group
              ].formOrder.findIndex(
                (form: any) => form.formId === video.form_id,
              );

              setSelectedFormStep(formStep);
              setFilledFormStep(formStep);
            }

            setFormModal(true);
          }
        }
        handleScrollToTop();
      };

      videoEl.addEventListener("ended", handleVideoEnd);

      return () => {
        videoEl.removeEventListener("ended", handleVideoEnd);
      };
    }
  }, [video, videoChoose, progressTransition]);

  //巻き戻しボタンが実行された時に、ProgressBarのアニメーションを再開する
  useEffect(() => {
    if (progress === 0) {
      setProgressTransition("0.3s linear");
    }
  }, [progress]);

  function sendMessageAndWaitForResponseFromLandingHub(
    message: any,
    url: string,
  ): Promise<any> {
    return new Promise((resolve, reject) => {
      function handleMessage(event: MessageEvent) {
        if (event.data && event.data.from === "LandingHub") {
          window.removeEventListener("message", handleMessage);
          resolve(event.data);
        }
      }
      window.addEventListener("message", handleMessage);
      window.parent.postMessage(message, url);
    });
  }

  function sendMessageAndWaitForResponseFromLandingHubVideo(
    message: any,
  ): Promise<any> {
    return new Promise((resolve, reject) => {
      const requestId = message.data.requestId;

      function handleMessage(event: MessageEvent) {
        if (
          event.data &&
          event.data.from === "LandingHub" &&
          event.data.request.data.requestId === requestId
        ) {
          window.removeEventListener("message", handleMessage);
          resolve(event.data);
        }
      }

      window.addEventListener("message", handleMessage);

      const targetOrigin = document.referrer || "*";
      window.parent.postMessage(message, targetOrigin);
    });
  }

  useEffect(() => {
    const handleParentWidth = (event: MessageEvent) => {
      if (event.data?.type === "vc_parent_width") {
        setIsParentMobile(event.data.width <= 767);
      }
    };
    window.addEventListener("message", handleParentWidth);
    return () => window.removeEventListener("message", handleParentWidth);
  }, []);

  useEffect(() => {
    if (!fontType) return;

    const style = document.createElement("style");

    const fontFaceMap: Record<string, string> = {
      "Noto Sans JP": `
        @font-face {
          font-family: "Noto Sans JP";
          src: url("${process.env.PUBLIC_URL}/assets/fonts/NotoSansJP-Regular.ttf") format("truetype");
          font-weight: 400;
          font-style: normal;
        }
      `,
      Tsunagi: `
        @font-face {
          font-family: "Tsunagi";
          src: url("${process.env.PUBLIC_URL}/assets/fonts/TsunagiGothic.ttf") format("truetype");
          font-weight: 400;
          font-style: normal;
        }
      `,
    };

    const selectedFont = fontFaceMap[fontType] || "";

    style.innerHTML = `
      ${selectedFont}
      body, * {
        font-family: "${fontType}", sans-serif !important;
      }
    `;

    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, [fontType]);

  //videoChooseIdから特定のvideoChooseとフォームデータを取得する
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        if (videoChooseId) {
          const db = getFirestore();
          const docRef = doc(db, "videoChooses", videoChooseId);
          const docSnap = await getDoc(docRef);

          if (!docSnap.exists()) {
            console.log("No such document!");
            return;
          }

          // 動画データとフォームデータを並列取得
          const videosCollectionRef = collection(docRef, "videos");
          const formsCollectionRef = collection(docRef, "forms");

          const [videosSnapshot, formsSnapshot] = await Promise.all([
            getDocs(videosCollectionRef),
            getDocs(formsCollectionRef),
          ]);

          // === 動画データの処理 ===
          let videos: videosType = {};
          let nextActions: nextActionsType = {};
          await Promise.all(
            videosSnapshot.docs.map(async (videoDoc) => {
              videos[videoDoc.id] = {
                id: videoDoc.id,
                nextActions: videoDoc.data().order,
                video_url: videoDoc.data().video_url,
                video_title: videoDoc.data().video_title,
                thumbnail: videoDoc.data().thumbnail,
                subtitle_url: videoDoc.data().subtitle_url || null,
                subtitle_text_color:
                  videoDoc.data().subtitle_text_color || null,
                subtitle_background_color:
                  videoDoc.data().subtitle_background_color || null,
                subtitle_font_family:
                  videoDoc.data().subtitle_font_family || null,
                annotation_url: videoDoc.data().annotation_url || null,
                is_auto_form_at_end:
                  videoDoc.data().is_auto_form_at_end || false,
                form_id: videoDoc.data().form_id || null,
                open_option_time: videoDoc.data().open_option_time || null,
                title_color: videoDoc.data().title_color || null,
                title_shadow_color: videoDoc.data().title_shadow_color || null,
                title_background_color:
                  videoDoc.data().title_background_color || null,
                back_button_color: videoDoc.data().back_button_color || null,
                back_button_shadow_color:
                  videoDoc.data().back_button_shadow_color || null,
                back_button_background_color:
                  videoDoc.data().back_button_background_color || null,
                subtitle_font_size: videoDoc.data().subtitle_font_size || 13,
                annotation_font_size:
                  videoDoc.data().annotation_font_size || 6.5,
                annotation_background_blur:
                  videoDoc.data().annotation_background_blur || false,
                hide_back_button: videoDoc.data().hide_back_button || false,
                subtitle_bold: videoDoc.data().subtitle_bold || false,
                title_font_size: videoDoc.data().title_font_size || null,
                title_bold: videoDoc.data().title_bold || false,
                back_button_font_size:
                  videoDoc.data().back_button_font_size || null,
                back_button_bold: videoDoc.data().back_button_bold || false,
              };

              if (videoDoc.data().auto_close_options) {
                setAutoCloseVideos((prev) => {
                  return [...prev, videoDoc.id];
                });
              }

              //サムネイルのみ先読み込み
              const img = new Image();
              img.src = videoDoc.data().thumbnail;

              const videoData = videoDoc.data();

              const nextActionsCollectionRef = collection(
                videoDoc.ref,
                "nextActions",
              );

              const nextActionsSnapshot = await getDocs(
                nextActionsCollectionRef,
              );
              nextActionsSnapshot.docs.forEach((doc) => {
                nextActions[doc.id] = { id: doc.id, ...doc.data() };
              });

              return { video_id: videoDoc.id, ...videoData, nextActions };
            }),
          );

          // === フォームデータの処理 ===
          await Promise.all(
            formsSnapshot.docs.map(async (formDoc) => {
              const fieldsCollectionRef = collection(formDoc.ref, "fields");
              const fieldsSnapshot = await getDocs(fieldsCollectionRef);
              const fields: FormFields = fieldsSnapshot.docs.reduce(
                (acc, fieldDoc) => {
                  const field = {
                    id: fieldDoc.id,
                    type: fieldDoc.data().type,
                    fieldName: fieldDoc.data().fieldName,
                    label: fieldDoc.data().label,
                    required: fieldDoc.data().required,
                    ...(fieldDoc.data().options
                      ? { options: fieldDoc.data().options }
                      : {}),
                    ...(fieldDoc.data().radioOptions
                      ? { radioOptions: fieldDoc.data().radioOptions }
                      : {}),
                    ...(fieldDoc.data().defaultValue
                      ? { defaultValue: fieldDoc.data().defaultValue }
                      : {}),
                    ...(fieldDoc.data().fieldCustomId
                      ? { fieldCustomId: fieldDoc.data().fieldCustomId }
                      : {}),
                    ...(fieldDoc.data().isConditionalDisplay !== undefined
                      ? {
                          isConditionalDisplay:
                            fieldDoc.data().isConditionalDisplay,
                          conditionField: fieldDoc.data().conditionField,
                          conditionFieldValues:
                            fieldDoc.data().conditionFieldValues,
                        }
                      : {}),
                    ...(fieldDoc.data().isTooltip !== undefined
                      ? {
                          isTooltip: fieldDoc.data().isTooltip,
                          toolTipText: fieldDoc.data().toolTipText,
                        }
                      : {}),
                    ...(fieldDoc.data().selectMultiple !== undefined
                      ? {
                          selectMultiple: fieldDoc.data().selectMultiple,
                        }
                      : {}),
                    ...(fieldDoc.data().checkboxDescription
                      ? {
                          checkboxDescription:
                            fieldDoc.data().checkboxDescription,
                        }
                      : {}),
                    ...(fieldDoc.data().richText
                      ? {
                          richText: fieldDoc.data().richText,
                        }
                      : {}),
                    ...(fieldDoc.data().imageUrl
                      ? {
                          imageUrl: fieldDoc.data().imageUrl,
                        }
                      : {}),
                    ...(fieldDoc.data().videoUrl
                      ? {
                          videoUrl: fieldDoc.data().videoUrl,
                        }
                      : {}),
                    ...(fieldDoc.data().minDateType !== undefined
                      ? {
                          minDateType: fieldDoc.data().minDateType,
                          minDateDaysAfter: fieldDoc.data().minDateDaysAfter,
                          minSelectedDate: fieldDoc.data().minSelectedDate,
                        }
                      : {}),
                    ...(fieldDoc.data().maxDateType !== undefined
                      ? {
                          maxDateType: fieldDoc.data().maxDateType,
                          maxDateDaysAfter: fieldDoc.data().maxDateDaysAfter,
                          maxSelectedDate: fieldDoc.data().maxSelectedDate,
                        }
                      : {}),
                    optionsHtml: fieldDoc.data().optionsHtml,
                    optionDisplayType: fieldDoc.data().optionDisplayType,
                    hiragana: fieldDoc.data().hiragana,
                    skipSecurityCode: fieldDoc.data().skipSecurityCode,
                    imageWidth: fieldDoc.data().imageWidth,
                    videoWidth: fieldDoc.data().videoWidth,
                    ...(fieldDoc.data().customHtml
                      ? { customHtml: fieldDoc.data().customHtml }
                      : {}),
                  };
                  return { ...acc, [fieldDoc.id]: field };
                },
                {},
              );

              const form: IVideoChooseForm = {
                [formDoc.id]: {
                  title: formDoc.data().title,
                  button_text: formDoc.data().button_text,
                  description: formDoc.data().description,
                  fields: fields,
                  send_color: formDoc.data().send_color,
                  order: formDoc.data().order,
                  is_title_displayed: formDoc.data().is_title_displayed,
                  send_type: formDoc.data().send_type
                    ? formDoc.data().send_type
                    : "submit",
                  video_id: formDoc.data().video_id
                    ? formDoc.data().video_id
                    : null,
                  complete_submit_title: formDoc.data().complete_submit_title,
                  complete_submit_description:
                    formDoc.data().complete_submit_description,
                  custom_script_id: formDoc.data().custom_script_id,
                  is_action_with_custom_script:
                    formDoc.data().is_action_with_custom_script,
                  action_type: formDoc.data().action_type,
                  is_waiting_validation: formDoc.data().is_waiting_validation
                    ? true
                    : false,
                  form_group: formDoc.data().form_group,
                  is_custom_script_after_submission: formDoc.data()
                    .is_custom_script_after_submission
                    ? true
                    : false,
                  custom_script_id_after_submission:
                    formDoc.data().custom_script_id_after_submission,
                  is_form_sync_with_submit: formDoc.data()
                    .is_form_sync_with_submit
                    ? true
                    : false,
                  conditions: formDoc.data().conditions,
                  form_background_color: formDoc.data().form_background_color,
                  form_background_opacity:
                    formDoc.data().form_background_opacity,
                  hide_progress: formDoc.data().hide_progress,
                  select_target_item: formDoc.data().select_target_item,
                },
              };

              setForms((prevForms) => {
                return { ...prevForms, ...form };
              });
            }),
          );

          // === 設定情報の処理 ===
          const videosOrdered = docSnap.data().order;
          const formGroups = docSnap.data().form_groups;

          setIsMuted(docSnap.data()?.default_muted || false);
          if (!!docSnap.data()?.default_muted) {
            setAudioPulseClass("video-choose-audio-pulse-button");
          }
          setShareLink(docSnap.data()?.link_option || false);
          setChangeSpeedButton(docSnap.data()?.change_speed_button || true);
          setAllowExpandButton(docSnap.data()?.allow_expand_button || false);
          setPlaybackRate(docSnap.data()?.default_speed || 1.0);
          setHideCopyright(docSnap.data()?.hide_copyright);
          setCloseButtonColor(docSnap.data()?.close_button_color || "#fff");
          setCloseButtonPosition(
            docSnap.data()?.close_button_position || "top-right",
          );
          setIsCloseDialogOpen(docSnap.data()?.is_close_dialog_open || false);

          const customHtmlAction = Object.values(nextActions).find(
            (action: any) => action.type === "custom_html",
          );
          if (customHtmlAction) {
            // 別ファイル方式（custom_html_content_url）または既存方式（custom_html_content）をサポート
            const loadCustomHtml = async () => {
              try {
                let htmlContent = "";

                if ((customHtmlAction as any).custom_html_content_url) {
                  // 別ファイル方式：URLからHTML/TXTファイルを取得
                  htmlContent = await fetchCustomHtmlFromUrl(
                    (customHtmlAction as any).custom_html_content_url,
                  );
                } else if ((customHtmlAction as any).custom_html_content) {
                  // 既存方式：Firestoreから直接取得
                  htmlContent = (customHtmlAction as any).custom_html_content;
                }

                if (htmlContent) {
                  preloadCustomHtmlImage(htmlContent);
                  setSelectedCustomHtml(htmlContent);
                  setSelectedCustomHtmlActionId(
                    (customHtmlAction as any).id || "",
                  );
                }
              } catch (error) {
                console.error("Error loading custom HTML:", error);
              }
            };
            loadCustomHtml();
          }

          setDomain(docSnap.data().domain || null);
          setChangedUrl(docSnap.data().changed_url || null);

          const launcherSettings = {
            buttonBorderColor: docSnap.data().button_border_color || "#09b871",
            buttonFromBottomPc: docSnap.data().button_from_bottom_pc || 15,
            buttonFromBottomSp: docSnap.data().button_from_bottom_sp || 10,
            buttonPosition: docSnap.data().button_position || "right",
            isCustomLaunch: docSnap.data().is_custom_launch ? "true" : "false",
            launchScrollPosition: docSnap.data().launch_scroll_position || 1,
            isSoundNotice: docSnap.data().is_sound_notice ? "true" : "false",
            soundNoticeText: docSnap.data().sound_notice_text || "動画でご説明",
            isButtonText: docSnap.data().is_button_text ? "true" : "false",
            buttonText: docSnap.data().button_text || "",
            isButtonMovingUp: docSnap.data().is_button_moving_up
              ? "true"
              : "false",
            buttonMovingUpHeight: docSnap.data().button_moving_up_height || 0,
            isCloseBtn: docSnap.data().is_close_btn ? "true" : "false",
            isLauncherCloseBtn: docSnap.data().is_launcher_close_btn
              ? "true"
              : "false",
            isBackButtonInIframe: docSnap.data().is_back_button_in_iframe
              ? "true"
              : "false",
            videoSource: docSnap.data().video_source || "",
            videoSourceImage: docSnap.data().video_source_image || "",
            videoChooseLauncherZIndex: docSnap.data().launcher_z_index || 995,
            videoChooseContainerZIndex: docSnap.data().container_z_index || 996,
            messageHideDelay: docSnap.data().message_hide_delay || 10000,
            messageShowAgainDelay:
              docSnap.data().message_show_again_delay || 10000,
            videoChooseShowDelay: docSnap.data().videochoose_show_delay || 10,
            isMessageShowAgain: docSnap.data().is_message_show_again
              ? "true"
              : "false",
            autoLaunchPc: docSnap.data().auto_launch_pc ? "true" : "false",
            emptyVideoChoose: docSnap.data().empty_videochoose
              ? "true"
              : "false",
            getCtaByHref: docSnap.data().get_cta_by_href || "",
            getCtaByClass: docSnap.data().get_cta_by_class || "",
            getCtaById: docSnap.data().get_cta_by_id || "",
            preventByHref: docSnap.data().prevent_by_href || "",
            preventByClass: docSnap.data().prevent_by_class || "",
            preventById: docSnap.data().prevent_by_id || "",
          };

          // === LandingHub連携処理 ===
          let parentLandingHub: any = null;
          if (videos && hasLandingHub) {
            try {
              const connectRes =
                await sendMessageAndWaitForResponseFromLandingHub(
                  {
                    action: "request",
                    command: "connect",
                    to: "LandingHub",
                    from: "video_choose",
                  },
                  document.referrer,
                );
              parentLandingHub = connectRes.data.result;
            } catch (e) {
              console.warn("LandingHub接続失敗", e);
              parentLandingHub = null;
            }

            if (parentLandingHub) {
              const optimizedVideos = await Promise.all(
                Object.entries(videos).map(async ([videoId, video]) => {
                  if (!video.video_url) return video;

                  try {
                    const requestId = crypto.randomUUID();
                    const lh_response =
                      await sendMessageAndWaitForResponseFromLandingHubVideo({
                        action: "request",
                        command: "optimize",
                        to: "LandingHub",
                        from: "video_choose",
                        data: {
                          source: "video",
                          src: video.video_url,
                          poster: video.thumbnail,
                          baseURI: window.location.href,
                          requestId: requestId,
                        },
                      });

                    return {
                      ...video,
                      video_url: lh_response.data.src,
                      thumbnail: lh_response.data.poster,
                    };
                  } catch (error) {
                    return video;
                  }
                }),
              );

              videos = optimizedVideos.reduce((acc, video) => {
                return { ...acc, [video.id]: video };
              }, {});
            }
          }

          // === videoChoose Stateの設定 ===
          setVideoChoose({
            videos,
            nextActions,
            videosOrdered,
            formGroups,
            formType: docSnap.data().form_type || "default",
            displaySettings: {
              launcherSettings,
              showClickedOptionOpacity:
                docSnap.data().show_clicked_option_opacity ?? false,
            },
          });
        }
      } catch (error: any) {
        if (error.code === "permission-denied") {
          console.log(
            "Access denied: You don't have permission to access this data.",
          );
        } else {
          console.error("Error fetching data:", error);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [videoChooseId]);

  /*
  videoChooseが変更された時に、最初のvideoをセットする
  さらに、historyに最初のvideoを追加する
  */
  useEffect(() => {
    setVideo(videoChoose.videos[videoChoose.videosOrdered[0]]);
    if (videoChoose.videosOrdered[0]) {
      setHistory((prevHistory) => [
        ...prevHistory,
        videoChoose.videosOrdered[0],
      ]);
    }
  }, [videoChoose]);

  /*
  videoに変更があった時に再生を開始する。
  ただし、autoPlayがfalse(初期状態)の時は再生しない
  ProgressBarのアニメーションを再開する
  */
  useEffect(() => {
    // 動画が変わったらdurationをリセット
    setVideoDuration(0);

    if (video && videoRef.current && autoPlay) {
      videoRef.current.play();
      setProgressTransition("0.3s linear");

      // 動画切替時の vc_video_play 発火 (2本目以降の動画到達計測用)
      // 1本目は autoPlay=false の初期マウント時には発火せず、embed-tag からの "play" メッセージ受信時の既存ハンドラに任せる
      if (process.env.REACT_APP_PROD && videoChooseId && video.id) {
        window.parent.postMessage(
          {
            action: "vc_video_play",
            videoId: video.id,
            videoChooseId: videoChooseId,
            domain: domain,
            currentTime: 0,
          },
          "*",
        );
      }

      // if (autoClose) {
      //   handleScrollToBottom();
      // }
      if (autoCloseVideos.includes(video.id)) {
        handleScrollToBottom();
      }
    }
  }, [video]);

  useEffect(() => {
    if (video && videoRef.current && playbackRate) {
      videoRef.current.playbackRate = playbackRate;
    }
  }, [video, playbackRate]);

  // プログレスバーのドラッグ時のグローバルイベント処理
  useEffect(() => {
    const handleGlobalMouseUp = () => {
      if (isDraggingProgress) {
        setIsDraggingProgress(false);
        setTimeout(() => {
          setProgressTransition("0.3s linear");
        }, 100);
      }
    };

    const handleGlobalTouchEnd = () => {
      if (isDraggingProgress) {
        setIsDraggingProgress(false);
        setTimeout(() => {
          setProgressTransition("0.3s linear");
        }, 100);
      }
    };

    if (isDraggingProgress) {
      window.addEventListener("mouseup", handleGlobalMouseUp);
      window.addEventListener("touchend", handleGlobalTouchEnd);
    }

    return () => {
      window.removeEventListener("mouseup", handleGlobalMouseUp);
      window.removeEventListener("touchend", handleGlobalTouchEnd);
    };
  }, [isDraggingProgress]);

  //iframeで実行された場合
  useEffect(() => {
    const handleReceiveMessage = (event: MessageEvent<MessageData>) => {
      if (
        (typeof event.data !== "object" ||
          event.data === null ||
          !event.data.action) &&
        consumerToken &&
        visitToken &&
        videoChooseId &&
        domain !== undefined
      ) {
        return;
      }

      const data = event.data;

      switch (data.action) {
        case "play":
          // emptyVideoChooseがtrueの場合は再生しない
          if (
            videoChoose.displaySettings.launcherSettings.emptyVideoChoose ===
            "true"
          ) {
            return;
          }
          videoRef.current?.play();
          fadeOutButton();
          if (video && autoCloseVideos.includes(video.id)) {
            handleScrollToBottom();
            setIsShowButton(true);
          } else {
            handleScrollToTop();
          }
          setPlayButtonClass("");
          setTimeout(() => {
            setAudioPulseClass("");
          }, 7000);
          if (
            process.env.REACT_APP_PROD &&
            videoChooseId &&
            video?.id &&
            !playLogRequest
          ) {
            // logRequest.play({
            //   consumerToken: consumerToken,
            //   visitToken: visitToken,
            //   videoChooseId: videoChooseId,
            //   videoId: video.id,
            //   viewedAt: getJSTDate(),
            // });
            setPlayLogRequest(true);
          }

          if (process.env.REACT_APP_PROD && videoChooseId) {
            window.parent.postMessage(
              {
                action: "setVcCvCookie",
                // consumerToken: consumerToken,
                // visitToken: visitToken,
                // videoChooseId: videoChooseId,
                // viewedAt: getJSTDate(),
                measurementTag: "via-video-only",
                videoChooseId: videoChooseId,
                domain: domain,
                getCtaByHref:
                  videoChoose.displaySettings.launcherSettings.getCtaByHref,
                getCtaByClass:
                  videoChoose.displaySettings.launcherSettings.getCtaByClass,
                getCtaById:
                  videoChoose.displaySettings.launcherSettings.getCtaById,
              },
              "*",
            );
            window.parent.postMessage(
              {
                action: "vc_play",
                videoChooseId: videoChooseId,
                domain: domain,
              },
              "*",
            );
            window.parent.postMessage(
              {
                action: "vc_video_play",
                videoId: video?.id,
                videoChooseId: videoChooseId,
                domain: domain,
                currentTime: videoRef.current?.currentTime || 0,
              },
              "*",
            );
          }

          break;
        case "pause":
          videoRef.current?.pause();

          // 停止イベントを親ページに送信
          window.parent.postMessage(
            {
              action: "vc_video_pause",
              videoId: video?.id,
              videoChooseId: videoChooseId,
              domain: domain,
              currentTime: videoRef.current?.currentTime || 0,
            },
            "*",
          );

          break;
        case "openIFrame":
          if (video && autoCloseVideos.includes(video.id)) {
            handleScrollToBottom();
            setIsShowButton(true);
          } else {
            handleScrollToTop();
          }
          break;
        case "openForm":
          if (data.form_id) {
            setSelectedFormId(data.form_id);

            if (
              forms[data.form_id] &&
              forms[data.form_id]?.form_group !== undefined
            ) {
              const formGroup = forms[data.form_id].form_group;
              if (
                formGroup !== undefined &&
                videoChoose.formGroups[formGroup]
              ) {
                const formStep = videoChoose.formGroups[
                  formGroup
                ].formOrder.findIndex(
                  (form: any) => form.formId === data.form_id,
                );
                setSelectedFormStep(formStep);
                setFilledFormStep(formStep);
              }
            }

            window.parent.postMessage(
              {
                action: "setVcCvCookie",
                // consumerToken: consumerToken,
                // visitToken: visitToken,
                // videoChooseId: videoChooseId,
                // viewedAt: getJSTDate(),
                measurementTag: "via-form-only",
                videoChooseId: videoChooseId,
                domain: domain,
                getCtaByHref:
                  videoChoose.displaySettings.launcherSettings.getCtaByHref,
                getCtaByClass:
                  videoChoose.displaySettings.launcherSettings.getCtaByClass,
                getCtaById:
                  videoChoose.displaySettings.launcherSettings.getCtaById,
              },
              "*",
            );

            setFormModal(true);
          }
          break;
        case "closeForm":
          handleFormModalClose();
          break;
        case "loading":
          setIframeLoading(true);
          //5秒後にローディングを解除
          setTimeout(() => {
            setIframeLoading(false);
          }, 5000);
          break;

        case "withdrawal_play":
          // emptyVideoChooseがtrueの場合は再生しない
          if (
            videoChoose.displaySettings.launcherSettings.emptyVideoChoose ===
            "true"
          ) {
            return;
          }
          videoRef.current?.play();
          fadeOutButton();
          if (video && autoCloseVideos.includes(video.id)) {
            handleScrollToBottom();
            setIsShowButton(true);
          } else {
            handleScrollToTop();
          }
          setPlayButtonClass("");
          setTimeout(() => {
            setAudioPulseClass("");
          }, 7000);
          if (
            process.env.REACT_APP_PROD &&
            videoChooseId &&
            video?.id &&
            !playLogRequest
          ) {
            // logRequest.play({
            //   consumerToken: consumerToken,
            //   visitToken: visitToken,
            //   videoChooseId: videoChooseId,
            //   videoId: video.id,
            //   viewedAt: getJSTDate(),
            // });
            setPlayLogRequest(true);
          }

          if (process.env.REACT_APP_PROD && videoChooseId) {
            window.parent.postMessage(
              {
                action: "setVcCvCookie",
                measurementTag: "via-video-only",
                withdrawal_play: "play",
                videoChooseId: videoChooseId,
                domain: domain,
                getCtaByHref:
                  videoChoose.displaySettings.launcherSettings.getCtaByHref,
                getCtaByClass:
                  videoChoose.displaySettings.launcherSettings.getCtaByClass,
                getCtaById:
                  videoChoose.displaySettings.launcherSettings.getCtaById,
              },
              "*",
            );
            window.parent.postMessage(
              {
                action: "vc_withdrawal_play",
                videoChooseId: videoChooseId,
                domain: domain,
              },
              "*",
            );
          }
          break;
        default:
      }
    };
    window.addEventListener("message", handleReceiveMessage);
    return () => {
      window.removeEventListener("message", handleReceiveMessage);
    };
  }, [
    videoChooseId,
    video,
    autoCloseVideos,
    domain,
    consumerToken,
    visitToken,
  ]);

  useEffect(() => {
    if (domain !== undefined && changedUrl !== undefined) {
      if (
        video &&
        videoRef.current &&
        visitToken &&
        videoChooseId &&
        consumerToken
      ) {
        if (!vcReadyPostMessage) {
          window.parent.postMessage(
            {
              action: "vcReady",
              consumerToken: consumerToken,
              visitToken: visitToken,
              // videoChooseId: videoChooseId,
              // viewedAt: getJSTDate(),
              measurementTag: "via-visit-only",
              domain: domain,
              changedUrl: changedUrl,
              videoChooseId: videoChooseId,
              ...(vcjsVersion == "v2.0.0"
                ? {
                    buttonBorderColor:
                      videoChoose.displaySettings.launcherSettings
                        .buttonBorderColor,
                    buttonFromBottomPc:
                      videoChoose.displaySettings.launcherSettings
                        .buttonFromBottomPc,
                    buttonFromBottomSp:
                      videoChoose.displaySettings.launcherSettings
                        .buttonFromBottomSp,
                    buttonPosition:
                      videoChoose.displaySettings.launcherSettings
                        .buttonPosition,
                    isCustomLaunch:
                      videoChoose.displaySettings.launcherSettings
                        .isCustomLaunch,
                    launchScrollPosition:
                      videoChoose.displaySettings.launcherSettings
                        .launchScrollPosition,
                    isSoundNotice:
                      videoChoose.displaySettings.launcherSettings
                        .isSoundNotice,
                    soundNoticeText:
                      videoChoose.displaySettings.launcherSettings
                        .soundNoticeText,
                    isButtonText:
                      videoChoose.displaySettings.launcherSettings.isButtonText,
                    buttonText:
                      videoChoose.displaySettings.launcherSettings.buttonText,
                    isButtonMovingUp:
                      videoChoose.displaySettings.launcherSettings
                        .isButtonMovingUp,
                    buttonMovingUpHeight:
                      videoChoose.displaySettings.launcherSettings
                        .buttonMovingUpHeight,
                    isLauncherCloseBtn:
                      videoChoose.displaySettings.launcherSettings
                        .isLauncherCloseBtn,
                    isBackButtonInIframe:
                      videoChoose.displaySettings.launcherSettings
                        .isBackButtonInIframe,
                    videoSource:
                      videoChoose.displaySettings.launcherSettings.videoSource,
                    videoSourceImage:
                      videoChoose.displaySettings.launcherSettings
                        .videoSourceImage,
                    videoChoose,
                    videoChooseContainerZIndex:
                      videoChoose.displaySettings.launcherSettings
                        .videoChooseContainerZIndex,
                    videoChooseLauncherZIndex:
                      videoChoose.displaySettings.launcherSettings
                        .videoChooseLauncherZIndex,
                    isMessageShowAgain:
                      videoChoose.displaySettings.launcherSettings
                        .isMessageShowAgain,
                    messageShowAgainDelay:
                      videoChoose.displaySettings.launcherSettings
                        .messageShowAgainDelay,
                    messageHideDelay:
                      videoChoose.displaySettings.launcherSettings
                        .messageHideDelay,
                    videoChooseShowDelay:
                      videoChoose.displaySettings.launcherSettings
                        .videoChooseShowDelay,
                    preventByHref:
                      videoChoose.displaySettings.launcherSettings
                        .preventByHref,
                    preventByClass:
                      videoChoose.displaySettings.launcherSettings
                        .preventByClass,
                    preventById:
                      videoChoose.displaySettings.launcherSettings.preventById,
                  }
                : {}),
            },
            "*",
          );

          window.parent.postMessage(
            {
              action: "vcABReady",
              consumerToken: consumerToken,
              visitToken: visitToken,
              measurementTag: "via-visit-only",
              domain: domain,
              changedUrl: changedUrl,
              videoChooseId: videoChooseId,
              buttonBorderColor:
                videoChoose.displaySettings.launcherSettings.buttonBorderColor,
              buttonFromBottomPc:
                videoChoose.displaySettings.launcherSettings.buttonFromBottomPc,
              buttonFromBottomSp:
                videoChoose.displaySettings.launcherSettings.buttonFromBottomSp,
              buttonPosition:
                videoChoose.displaySettings.launcherSettings.buttonPosition,
              isCustomLaunch:
                videoChoose.displaySettings.launcherSettings.isCustomLaunch,
              launchScrollPosition:
                videoChoose.displaySettings.launcherSettings
                  .launchScrollPosition,
              isSoundNotice:
                videoChoose.displaySettings.launcherSettings.isSoundNotice,
              soundNoticeText:
                videoChoose.displaySettings.launcherSettings.soundNoticeText,
              isButtonText:
                videoChoose.displaySettings.launcherSettings.isButtonText,
              buttonText:
                videoChoose.displaySettings.launcherSettings.buttonText,
              isButtonMovingUp:
                videoChoose.displaySettings.launcherSettings.isButtonMovingUp,
              buttonMovingUpHeight:
                videoChoose.displaySettings.launcherSettings
                  .buttonMovingUpHeight,
              isLauncherCloseBtn:
                videoChoose.displaySettings.launcherSettings.isLauncherCloseBtn,
              isBackButtonInIframe:
                videoChoose.displaySettings.launcherSettings
                  .isBackButtonInIframe,
              videoSource:
                videoChoose.displaySettings.launcherSettings.videoSource,
              videoSourceImage:
                videoChoose.displaySettings.launcherSettings.videoSourceImage,
              videoChoose,
              videoChooseContainerZIndex:
                videoChoose.displaySettings.launcherSettings
                  .videoChooseContainerZIndex,
              videoChooseLauncherZIndex:
                videoChoose.displaySettings.launcherSettings
                  .videoChooseLauncherZIndex,
              isMessageShowAgain:
                videoChoose.displaySettings.launcherSettings.isMessageShowAgain,
              messageShowAgainDelay:
                videoChoose.displaySettings.launcherSettings
                  .messageShowAgainDelay,
              messageHideDelay:
                videoChoose.displaySettings.launcherSettings.messageHideDelay,
              videoChooseShowDelay:
                videoChoose.displaySettings.launcherSettings
                  .videoChooseShowDelay,
              preventByHref:
                videoChoose.displaySettings.launcherSettings.preventByHref,
              preventByClass:
                videoChoose.displaySettings.launcherSettings.preventByClass,
              preventById:
                videoChoose.displaySettings.launcherSettings.preventById,
              emptyVideoChoose:
                videoChoose.displaySettings.launcherSettings.emptyVideoChoose,
            },
            "*",
          );

          window.parent.postMessage(
            {
              action: "vc_page_view",
              domain: domain,
              videoChooseId: videoChooseId,
            },
            "*",
          );
        }
        setVcReadyPostMessage(true);

        if (process.env.REACT_APP_PROD) {
          if (!visitLogRequest && videoChooseId) {
            // logRequest.visit({
            //   consumerToken: consumerToken,
            //   visitToken: visitToken,
            //   videoChooseId: videoChooseId,
            //   viewedAt: getJSTDate(),
            // });
          }
          setVisitLogRequest(true);
        }
      }
    }
  }, [video, visitToken, videoChooseId, domain, consumerToken, changedUrl]);

  //再生ボタンを押した後にfadeout
  const fadeOutButton = () => {
    let opacity = 1;
    const fadeOutInterval = setInterval(() => {
      if (opacity <= 0) {
        clearInterval(fadeOutInterval);
      } else {
        opacity -= 0.15;
        setPlayButtonOpacity(opacity);
      }
    }, 1);
  };

  useEffect(() => {
    if (domain !== undefined) {
      if (video && videoRef.current) {
        if (firstAutoPlay) {
          // emptyVideoChooseがtrueの場合は自動再生しない
          if (
            videoChoose.displaySettings.launcherSettings.emptyVideoChoose ===
            "true"
          ) {
            return;
          }
          videoRef.current.muted = true;
          videoRef.current.play();
          setProgressTransition("0.3s linear");
          fadeOutButton();
          if (autoCloseVideos.includes(video.id)) {
            handleScrollToBottom();
          }
          setTimeout(() => {
            setFirstAutoPlay(false);
          }, 7000);
          setAudioPulseClass("video-choose-audio-pulse-button");
          setIsMuted(true);
          setTimeout(() => {
            setAudioPulseClass("");
          }, 7000);

          if (
            isFirstAutoPlay &&
            process.env.REACT_APP_PROD &&
            videoChooseId &&
            video?.id
          ) {
            window.parent.postMessage(
              {
                action: "setVcCvCookie",
                // consumerToken: consumerToken,
                // visitToken: visitToken,
                // videoChooseId: videoChooseId,
                // viewedAt: getJSTDate(),
                videoChooseId: videoChooseId,
                measurementTag: "via-auto-video-only",
                domain: domain,
                getCtaByHref:
                  videoChoose.displaySettings.launcherSettings.getCtaByHref,
                getCtaByClass:
                  videoChoose.displaySettings.launcherSettings.getCtaByClass,
                getCtaById:
                  videoChoose.displaySettings.launcherSettings.getCtaById,
              },
              "*",
            );
          }

          if (
            process.env.REACT_APP_PROD &&
            videoChooseId &&
            video?.id &&
            !playLogRequest
          ) {
            // logRequest.play({
            //   consumerToken: consumerToken,
            //   visitToken: visitToken,
            //   videoChooseId: videoChooseId,
            //   videoId: video.id,
            //   viewedAt: getJSTDate(),
            // });
            setPlayLogRequest(true);
          }
        }
      }
    }
  }, [video, firstAutoPlay, domain]);

  //Clipboardにリンクをコピーする
  const copyLinkToClipboard = async () => {
    const currentPageUrl = `${window.location.origin}${window.location.pathname}`;

    try {
      await navigator.clipboard.writeText(currentPageUrl);
      enqueueSnackbar("リンクをコピーしました", {
        anchorOrigin: { vertical: "top", horizontal: "center" },
      });
      if (videoChooseId && video?.id) {
        if (process.env.REACT_APP_PROD) {
          // logRequest.copyLink({
          //   consumerToken: consumerToken,
          //   visitToken: visitToken,
          //   videoChooseId: videoChooseId,
          //   videoId: video.id,
          //   copiedAt: getJSTDate(),
          // });
        }
      }
    } catch (err) {
      console.error("Failed to copy: ", err);
      enqueueSnackbar("コピーに失敗しました", {
        variant: "error",
        anchorOrigin: { vertical: "top", horizontal: "center" },
      });
    }
  };

  const handleFormModalClose = async () => {
    setFormModal(false);
    setSelectedFormId("");
  };

  const handleCustomHtmlModalClose = () => {
    setCustomHtmlModal(false);
    setSelectedCustomHtmlActionId("");
  };

  const handleCustomHtmlButtonClick = () => {
    if (!selectedCustomHtmlActionId) {
      handleCustomHtmlModalClose();
      return;
    }

    const action = videoChoose.nextActions[selectedCustomHtmlActionId];

    if (!action) {
      handleCustomHtmlModalClose();
      return;
    }

    if (action.custom_script_id) {
      sendCostomScriptIdMessage(action.custom_script_id, videoChooseId);
    }

    handleCustomHtmlModalClose();
  };

  const handleCustomHtmlContentClick = (
    event: React.MouseEvent<HTMLDivElement>,
  ) => {
    const target = event.target as HTMLElement;
    const buttonElement = target.closest("[data-vc-custom-button]");

    if (!buttonElement) {
      return;
    }

    event.preventDefault();
    handleCustomHtmlButtonClick();
  };

  // HTML/TXTファイルを取得する関数（別ファイル方式）
  const fetchCustomHtmlFromUrl = useCallback(
    async (url: string): Promise<string> => {
      try {
        const response = await fetch(url);
        if (!response.ok) {
          throw new Error(
            `Failed to fetch HTML/TXT file: ${response.status} ${response.statusText}`,
          );
        }
        const htmlContent = await response.text();
        return htmlContent;
      } catch (error) {
        console.error("Error fetching custom HTML/TXT file from URL:", error);
        throw error;
      }
    },
    [],
  );

  const preloadCustomHtmlImage = useCallback((html: string) => {
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = html;
    const img = tempDiv.querySelector("img");
    if (img && img instanceof HTMLImageElement && img.src) {
      const preloadedImage = new Image();
      preloadedImage.src = img.src;
      if (preloadedImage.complete) {
        setCustomHtmlImageLoaded(true);
      } else {
        preloadedImage.onload = () => setCustomHtmlImageLoaded(true);
        preloadedImage.onerror = () => setCustomHtmlImageLoaded(true);
      }
    } else {
      setCustomHtmlImageLoaded(true);
    }
  }, []);

  const handleVideoTypeNextAction = (videoId: any) => {
    if (videoId !== undefined) {
      const nextVideo = videoChoose.videos[videoId];
      if (nextVideo) {
        setProgressTransition("none");
        setAutoPlay(true);
        setProgress(0);
        setVideoLoading(true);
        setOpacity(1);
        setVideo(nextVideo);
        setIsVideoEnd(false);
        setPlayButtonOpacity(0);

        if (videoId !== undefined) {
          setHistory((prevHistory) => {
            const currentIndex = video
              ? prevHistory.indexOf(video.id)
              : prevHistory.length - 1;
            const truncated = prevHistory.slice(0, currentIndex + 1);
            if (!truncated.includes(videoId)) {
              return [...truncated, videoId];
            }
            return truncated;
          });
        }
      }
    }
  };

  useEffect(() => {
    // vcReadyの親ページへのリクエストが終わった後にメッセージリスナーを登録
    if (!vcReadyPostMessage) {
      return;
    }

    function handleIframeMessage(event: any) {
      if (event.data.action === "parentHtmlData") {
        let updatedHtml = selectedCustomHtml;
        event.data.data?.forEach((item: any) => {
          const targetId = item.targetId;
          const regex = new RegExp(
            `(<([a-zA-Z0-9]+) id="${targetId}"[^>]*>)(.*?)(<\\/\\2>)`,
            "i",
          );
          updatedHtml = updatedHtml.replace(regex, `$1${item.textContent}$4`);
        });
        setSelectedCustomHtml(updatedHtml);
      }
    }

    window.addEventListener("message", handleIframeMessage);

    window.parent.postMessage(
      {
        action: "vcCustomHtmlReady",
        videoChooseId: videoChooseId,
      },
      "*",
    );

    return () => {
      window.removeEventListener("message", handleIframeMessage);
    };
  }, [vcReadyPostMessage, selectedCustomHtml]);

  if (loading) {
    return (
      <div className="content-gradient" style={commonStyles.gradientContainer}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100vh",
          }}
        >
          <CircularProgress
            size={32}
            style={{ color: "rgba(255,255,255, 0.8)" }}
          />
        </Box>
      </div>
    );
  }

  return (
    <div className="content-gradient" style={commonStyles.gradientContainer}>
      {/*Progressバー */}
      <Box
        sx={[
          commonStyles.progressBarBox,
          {
            width: isClose ? "calc(97% - 45px)" : "97%",
            left: isClose ? (mdUp ? "1.5%" : "10px") : "50%",
            transform: isClose ? "none" : "translateX(-50%)",
          },
        ]}
      >
        {history.length > 0 &&
          video &&
          history.map((his, index) => {
            // 現在再生中の動画かどうかを判定
            const isCurrentVideo = his === video?.id;

            // プログレスバーの位置を更新する共通関数
            const updateProgressPosition = (clientX: number, rect: DOMRect) => {
              if (!videoRef.current) return;

              const clickX = clientX - rect.left;
              const clickPosition = Math.max(
                0,
                Math.min(1, clickX / rect.width),
              );

              // クリック位置に応じて動画の再生位置を変更
              const newTime = clickPosition * videoRef.current.duration;
              videoRef.current.currentTime = newTime;

              // プログレスバーを即座に更新
              const newProgress = clickPosition * 100;
              setProgress(newProgress);
              setProgressTransition("none");
            };

            return (
              <Box
                key={his}
                sx={{
                  width: `calc((100% / ${history.length}) - 4px)`,
                  cursor: isCurrentVideo ? "pointer" : "default",
                  position: "relative",
                  height: isCurrentVideo ? "18px" : "2px",
                  display: "flex",
                  alignItems: "center",
                  marginTop: isCurrentVideo ? "-8px" : "0px",
                  marginBottom: isCurrentVideo ? "-8px" : "0px",
                }}
                onClick={(e) => {
                  // 現在再生中の動画のプログレスバーのみクリック可能
                  if (isCurrentVideo && videoRef.current) {
                    const rect = e.currentTarget.getBoundingClientRect();
                    updateProgressPosition(e.clientX, rect);

                    // アニメーションを再開
                    setTimeout(() => {
                      setProgressTransition("0.3s linear");
                    }, 100);
                  }
                }}
                onMouseDown={(e) => {
                  // マウスドラッグ開始
                  if (isCurrentVideo) {
                    setIsDraggingProgress(true);
                    const rect = e.currentTarget.getBoundingClientRect();
                    updateProgressPosition(e.clientX, rect);
                  }
                }}
                onMouseMove={(e) => {
                  // マウスドラッグ中
                  if (isCurrentVideo && isDraggingProgress) {
                    const rect = e.currentTarget.getBoundingClientRect();
                    updateProgressPosition(e.clientX, rect);
                  }
                }}
                onMouseUp={() => {
                  // マウスドラッグ終了
                  if (isDraggingProgress) {
                    setIsDraggingProgress(false);
                    setTimeout(() => {
                      setProgressTransition("0.3s linear");
                    }, 100);
                  }
                }}
                onMouseLeave={() => {
                  // マウスが要素外に出た時
                  if (isDraggingProgress) {
                    setIsDraggingProgress(false);
                    setTimeout(() => {
                      setProgressTransition("0.3s linear");
                    }, 100);
                  }
                }}
                onTouchStart={(e) => {
                  // タッチドラッグ開始
                  if (isCurrentVideo) {
                    setIsDraggingProgress(true);
                    const touch = e.touches[0];
                    const rect = e.currentTarget.getBoundingClientRect();
                    updateProgressPosition(touch.clientX, rect);
                  }
                }}
                onTouchMove={(e) => {
                  // タッチドラッグ中
                  if (isCurrentVideo && isDraggingProgress) {
                    const touch = e.touches[0];
                    const rect = e.currentTarget.getBoundingClientRect();
                    updateProgressPosition(touch.clientX, rect);
                  }
                }}
                onTouchEnd={() => {
                  // タッチドラッグ終了
                  if (isDraggingProgress) {
                    setIsDraggingProgress(false);
                    setTimeout(() => {
                      setProgressTransition("0.3s linear");
                    }, 100);
                  }
                }}
              >
                <LinearProgress
                  variant="determinate"
                  value={
                    his == video?.id
                      ? progress
                      : index >= history.indexOf(video.id)
                        ? 0
                        : 100
                  }
                  style={{
                    height: "2px",
                    backgroundColor: "grey",
                    width: "100%",
                  }}
                  sx={{
                    "& .MuiLinearProgress-barColorPrimary": {
                      backgroundColor: "white",
                      transition: progressTransition,
                    },
                  }}
                />
                {/* 現在位置を示す丸 */}
                {isCurrentVideo && (
                  <Box
                    sx={{
                      position: "absolute",
                      left: `${progress}%`,
                      top: "50%",
                      transform: "translate(-50%, -50%)",
                      width: "10px",
                      height: "10px",
                      backgroundColor: "white",
                      borderRadius: "50%",
                      boxShadow: "0 0 4px rgba(0, 0, 0, 0.5)",
                      transition: progressTransition,
                      pointerEvents: "none",
                      zIndex: 1,
                    }}
                  />
                )}
              </Box>
            );
          })}
      </Box>

      {/* 残り秒数表示 */}
      {/* {video && videoDuration > 0 && (
        <Box
          sx={{
            position: "absolute",
            top: 20,
            right: isClose
              ? mdUp
                ? "calc(1.5% + 40px)"
                : "calc(10px + 40px)"
              : "1.5%",
            color: "white",
            fontSize: "11px",
            fontWeight: 500,
            textShadow: "0 1px 2px rgba(0, 0, 0, 0.8)",
            zIndex: 520,
            pointerEvents: "none",
            userSelect: "none",
          }}
        >
          {(() => {
            const currentTime = (progress / 100) * videoDuration;
            const remainingSeconds = Math.max(
              0,
              Math.floor(videoDuration - currentTime)
            );
            const minutes = Math.floor(remainingSeconds / 60);
            const seconds = remainingSeconds % 60;
            return `${minutes}:${seconds.toString().padStart(2, "0")}`;
          })()}
        </Box>
      )} */}

      {/* iframeで読み込まれている場合は閉じるボタン */}
      {isClose && (
        <Box
          sx={{
            position: "absolute",
            ...(closeButtonPosition === "bottom-right"
              ? { bottom: 2, right: 4 }
              : { top: 2, right: 4 }),
            display: "flex",
            flexDirection: "row",
            zIndex: 550,
            width: closeButtonPosition === "bottom-right" ? 48 : 40,
            height: closeButtonPosition === "bottom-right" ? 48 : 40,
            minWidth: closeButtonPosition === "bottom-right" ? 48 : 40,
            minHeight: closeButtonPosition === "bottom-right" ? 48 : 40,
            justifyContent: "center",
            alignItems: "center",
            padding: 0,
            borderRadius: "50%",
            transform: "none",
            // backgroundColor: "rgba(0, 0, 0, 0.6)",
          }}
        >
          <Button
            disableRipple
            sx={{
              width: "100%",
              padding: 0,
              transition: "none",
              boxShadow: "none",
              "&:hover": {
                backgroundColor: "transparent",
              },
            }}
            onClick={() => {
              if (isCloseDialogOpen) {
                handleCloseDialogOpen();
              } else {
                handleDirectClose();
              }
            }}
          >
            <Iconify
              icon={"mingcute:close-fill"}
              width={closeButtonPosition === "bottom-right" ? 28 : 26}
              height={closeButtonPosition === "bottom-right" ? 28 : 26}
              color={"#fff"}
              sx={{ pointerEvents: "none" }}
            />
          </Button>
        </Box>
      )}

      {/* 画面上部のグラデーション影 */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "calc(var(--vh, 1vh) * 15)",
          backgroundImage: "linear-gradient(to bottom, #0000004d, #0000)",
          pointerEvents: "none",
          zIndex: 490,
        }}
      />

      {/* 閉じる確認ダイアログ */}
      <Dialog
        open={showCloseDialog}
        onClose={handleCloseDialogClose}
        aria-labelledby="close-dialog-title"
        aria-describedby="close-dialog-description"
        PaperProps={{
          sx: {
            borderRadius: 2,
            minWidth: 300,
            position: "fixed",
            top: "8%",
            left: "50%",
            transform: "translateX(-50%)",
            margin: 0,
          },
        }}
      >
        <DialogTitle
          id="close-dialog-title"
          sx={{ textAlign: "center", pb: 1 }}
        >
          動画を閉じますか？
        </DialogTitle>

        <DialogActions sx={{ justifyContent: "center", gap: 2, pb: 3 }}>
          <Button
            onClick={handleConfirmClose}
            variant="outlined"
            sx={{ minWidth: 100 }}
          >
            閉じる
          </Button>
          <Button
            onClick={handleCancelClose}
            variant="contained"
            color="primary"
            sx={{ minWidth: 100 }}
          >
            キャンセル
          </Button>
        </DialogActions>
      </Dialog>

      <Box sx={[commonStyles.titleBox, { width: mdUp ? "97%" : "95%" }]}>
        <Typography
          variant="caption"
          sx={[
            commonStyles.titleTypography,
            {
              color: video?.title_color ? video?.title_color : "#fff",
              fontSize: video?.title_font_size || 11,
              fontWeight: video?.title_bold ? 700 : 100,
              filter: `drop-shadow(0px 0px 1px ${video?.title_shadow_color || "#666"})`,
              ...(video?.title_background_color
                ? {
                    backgroundColor: video.title_background_color,
                    padding: "2px 4px 3px",
                    lineHeight: 1.3,
                    borderRadius: "3px",
                  }
                : {}),
            },
          ]}
        >
          {video?.video_title}
        </Typography>
      </Box>

      {/*バックボタンとツールバー */}
      <Box>
        {/*左のBackボタン*/}
        {history.length > 1 &&
          history[0] != video?.id &&
          !video?.hide_back_button && (
            <Box>
              <Button
                onClick={() => {
                  setProgressTransition("none");
                  setAutoPlay(true);
                  if (video) {
                    const currentVideoIndex = history.indexOf(video.id);
                    const prevVideoId = history[currentVideoIndex - 1];
                    if (prevVideoId !== undefined) {
                      const nextVideo = videoChoose.videos[prevVideoId];
                      if (nextVideo) {
                        setProgress(0);
                        setOpacity(1);
                        setIsVideoEnd(true);
                        setVideo(nextVideo);
                        setIsVideoEnd(false);
                        if (prevVideoId !== undefined) {
                          const videoId: string = prevVideoId;
                          setHistory((prevHistory) => {
                            if (!prevHistory.includes(videoId)) {
                              return [...prevHistory, videoId];
                            } else {
                              return prevHistory;
                            }
                          });
                        }
                      }
                    }
                  }
                  setPlayButtonOpacity(0);
                }}
                sx={[
                  commonStyles.backButton,
                  {
                    left: mdUp ? 25 : iframeXSdown ? 0 : 0,
                    top: mdUp ? 75 : iframeXSdown ? 60 : 60,
                    alignItems: "center",
                    display: "flex",
                    justifyContent: "center",
                    filter: `drop-shadow(0px 0px 1px ${video?.back_button_shadow_color || "#666"})`,
                    ...(video?.back_button_background_color
                      ? {
                          backgroundColor: video.back_button_background_color,
                          padding: "2px 4px 3px",
                          lineHeight: 1.3,
                          borderRadius: "3px",
                          left: mdUp ? "1.5%" : "2.5%",
                          minWidth: 60,
                        }
                      : {}),
                  },
                ]}
              >
                <Iconify
                  icon={"ep:arrow-left"}
                  width={((video?.back_button_font_size || 12) / 12) * 17}
                  style={{ position: "relative", top: 0 }}
                  color={
                    video?.back_button_color ? video?.back_button_color : "#fff"
                  }
                />
                <span
                  style={{
                    color: video?.back_button_color
                      ? video?.back_button_color
                      : "#fff",
                    fontSize: video?.back_button_font_size || 12,
                    fontWeight: video?.back_button_bold ? 700 : 100,
                  }}
                >
                  戻る
                </span>
              </Button>
            </Box>
          )}

        {/* 中央の注釈スペース*/}

        <Box
          sx={[
            commonStyles.backButton,
            {
              position: "absolute",
              zIndex: 500,
              padding: 0,
              top: mdUp ? 65 : iframeXSdown ? 40 : 40,
              alignItems: "center",
              display: "flex",
              justifyContent: "center",
              filter: video?.annotation_background_blur
                ? "none"
                : "drop-shadow(0px 0px 1px #999)",
              left: "50%",
              transform: "translateX(-50%)",
              width: "70%",
            },
          ]}
        >
          <Annotation
            annotationUrl={video?.annotation_url || null}
            currentTime={videoRef.current?.currentTime || 0}
            annotationFontSize={video?.annotation_font_size || 6.5}
            annotationBackgroundBlur={
              video?.annotation_background_blur || false
            }
          />
        </Box>

        {/*右のツールボタン*/}
        <Box>
          <Button
            onClick={() => {
              setIsMuted((prev) => {
                if (prev) {
                  setAudioPulseClass("");
                }
                return !prev;
              });
              if (firstAutoPlay) {
                setFirstAutoPlay(false);
              }
            }}
            className={audioPulseClass}
            style={{
              position: "absolute",
              top: mdUp ? 75 : iframeXSdown ? 50 : 55,
              right: mdUp ? 25 : 10,
              zIndex: 500,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              padding: 0,
              borderRadius: "50%",
              width: iframeXSdown ? 30 : 40,
              height: iframeXSdown ? 30 : 40,
              minWidth: iframeXSdown ? 30 : 40,
              minHeight: iframeXSdown ? 30 : 40,
              backgroundColor: "rgba(0, 0, 0, 0.5)",
              transform: "none",
            }}
          >
            <Iconify
              icon={
                isMuted
                  ? "fluent:speaker-mute-24-regular"
                  : "fluent:speaker-2-16-regular"
              }
              width={iframeXSdown ? 18 : 28}
              color={"rgba(255,255,255, 1)"}
            />
          </Button>

          {changeSpeedButton && (
            <Button
              onClick={() => {
                setPlaybackRate((prev) => {
                  if (prev === 1) {
                    changeSpeed(1.2);
                    return 1.2;
                  } else if (prev === 1.2) {
                    changeSpeed(1.5);
                    return 1.5;
                  } else if (prev === 1.5) {
                    changeSpeed(2);
                    return 2;
                  } else {
                    changeSpeed(1);
                    return 1;
                  }
                });
              }}
              style={{
                position: "absolute",
                top: mdUp ? 130 : iframeXSdown ? 90 : 105,
                right: mdUp ? 25 : 10,
                zIndex: 500,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                padding: 0,
                borderRadius: "50%",
                width: iframeXSdown ? 30 : 40,
                height: iframeXSdown ? 30 : 40,
                minWidth: iframeXSdown ? 30 : 40,
                minHeight: iframeXSdown ? 30 : 40,
                backgroundColor: "rgba(0, 0, 0, 0.5)",
                transform: "none",
              }}
            >
              <Iconify
                icon={
                  playbackRate === 1
                    ? "fluent:multiplier-1x-28-regular"
                    : playbackRate === 1.2
                      ? "fluent:multiplier-1-2x-20-regular"
                      : playbackRate === 1.5
                        ? "fluent:multiplier-1-5x-28-regular"
                        : "fluent:multiplier-2x-28-regular"
                }
                width={iframeXSdown ? 18 : 28}
                color={"rgba(255,255,255, 1)"}
              />
            </Button>
          )}

          {allowExpandButton && isParentMobile && (
            <Button
              onClick={() => {
                const next = !isExpanded;
                setIsExpanded(next);
                window.parent.postMessage(
                  { type: "vc_toggle_expand", expand: next },
                  "*",
                );
              }}
              style={{
                position: "absolute",
                top: mdUp ? 185 : iframeXSdown ? 130 : 155,
                right: mdUp ? 25 : 10,
                zIndex: 500,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                padding: 0,
                borderRadius: "50%",
                width: iframeXSdown ? 30 : 40,
                height: iframeXSdown ? 30 : 40,
                minWidth: iframeXSdown ? 30 : 40,
                minHeight: iframeXSdown ? 30 : 40,
                backgroundColor: "rgba(0, 0, 0, 0.5)",
                transform: "none",
              }}
            >
              <Iconify
                icon={
                  isExpanded
                    ? "material-symbols:close-fullscreen"
                    : "material-symbols:open-in-full"
                }
                width={iframeXSdown ? 16 : 22}
                color={"rgba(255,255,255,1)"}
              />
            </Button>
          )}

          {/*共有リンクボタン */}
          {/* {shareLink && (
            <Button
              onClick={copyLinkToClipboard}
              style={{
                position: "absolute",
                top: mdUp ? 185 : iframeXSdown ? 130 : 155,
                right: mdUp ? 25 : 10,
                zIndex: 500,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                padding: 0,
                borderRadius: "50%",
                width: iframeXSdown ? 30 : 40,
                height: iframeXSdown ? 30 : 40,
                minWidth: iframeXSdown ? 30 : 40,
                minHeight: iframeXSdown ? 30 : 40,
                backgroundColor: "rgba(0,0,0, 0.7)",
                transform: "none",
              }}
            >
              <Iconify
                icon={"material-symbols:share"}
                width={iframeXSdown ? 16 : 24}
                color={"rgba(255,255,255, 1)"}
                sx={{ position: "relative", right: 1 }}
              />
            </Button>
          )} */}
        </Box>
      </Box>

      {/*中央のボタン */}

      {/*再生*/}

      {/* {firstAutoPlay ? (
        <Box sx={[commonStyles.speakerButtonBox, { opacity: 1 }]}>
          <Button
            aria-label="Play"
            sx={[
              commonStyles.speakerButton,
              {
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                position: "relative",
                "&:hover": {
                  backgroundColor: "rgba(0,0,0, 0.5)",
                },
              },
            ]}
            className={"video-choose-speaker-button"}
            onClick={() => {
              if (videoRef.current) {
                videoRef.current.muted = false;
                setFirstAutoPlay(false);
                setIsMuted(false);
              }
            }}
          >
            <Typography
              style={{
                color: "#fff",
                fontSize: 12,
                letterSpacing: "-0.2px",
                paddingTop: "7px",
              }}
            >
              音声を聞く
            </Typography>
            <Iconify
              icon={"fluent:speaker-2-16-regular"}
              width={35}
              color={"rgba(255,255,255, 1)"}
            />
          </Button>
        </Box>
      ) : (
        <Box
          sx={[
            commonStyles.centerButton,
            {
              opacity: playButtonOpacity,
            },
          ]}
        >
          <Button
            aria-label="Play"
            style={{}}
            className={playButtonClass}
            onClick={() => {
              if (!videoRef.current?.paused) {
                videoRef.current?.pause();
                setPlayButtonOpacity(1);
                handleScrollToTop();
              } else {
                setAutoPlay(true);
                videoRef.current?.play();
                if (
                  process.env.REACT_APP_PROD &&
                  videoChooseId &&
                  video?.id &&
                  !playLogRequest
                ) {
                  logRequest.play({
                    consumerToken: consumerToken,
                    visitToken: visitToken,
                    videoChooseId: videoChooseId,
                    videoId: video?.id,
                    viewedAt: getJSTDate(),
                  });
                  setPlayLogRequest(true);
                }

                if (video && autoCloseVideos.includes(video.id)) {
                  handleScrollToBottom();
                }
                fadeOutButton();
                setPlayButtonClass("");
              }
            }}
          >
            <Iconify
              icon={"mdi:play"}
              width={50}
              color={"rgba(255,255,255, 1)"}
            />
          </Button>
        </Box>
      )} */}

      <Box
        sx={[
          commonStyles.centerButton,
          {
            opacity: playButtonOpacity,
          },
        ]}
      >
        <Button
          aria-label="Play"
          style={{}}
          className={`${playButtonClass} video-choose-button`}
          onClick={() => {
            if (!videoRef.current?.paused) {
              videoRef.current?.pause();
              setPlayButtonOpacity(1);
              handleScrollToTop();

              // 停止イベントを親ページに送信
              window.parent.postMessage(
                {
                  action: "vc_video_pause",
                  videoId: video?.id,
                  videoChooseId: videoChooseId,
                  domain: domain,
                  currentTime: videoRef.current?.currentTime || 0,
                },
                "*",
              );
            } else {
              // emptyVideoChooseがtrueの場合は再生しない
              if (
                videoChoose.displaySettings.launcherSettings
                  .emptyVideoChoose === "true"
              ) {
                return;
              }
              setAutoPlay(true);
              videoRef.current?.play();

              // 再生イベントを親ページに送信
              window.parent.postMessage(
                {
                  action: "vc_video_play",
                  videoId: video?.id,
                  videoChooseId: videoChooseId,
                  domain: domain,
                  currentTime: videoRef.current?.currentTime || 0,
                },
                "*",
              );

              if (
                process.env.REACT_APP_PROD &&
                videoChooseId &&
                video?.id &&
                !playLogRequest
              ) {
                // logRequest.play({
                //   consumerToken: consumerToken,
                //   visitToken: visitToken,
                //   videoChooseId: videoChooseId,
                //   videoId: video?.id,
                //   viewedAt: getJSTDate(),
                // });
                setPlayLogRequest(true);
              }

              if (video && autoCloseVideos.includes(video.id)) {
                handleScrollToBottom();
              }
              fadeOutButton();
              setPlayButtonClass("");
            }
          }}
        >
          <Iconify
            icon={"mdi:play"}
            width={50}
            color={"rgba(255,255,255, 1)"}
          />
        </Button>
      </Box>

      {/*巻き戻し */}
      {isVideoEnd && (
        <Box sx={commonStyles.centerButton}>
          <Button
            aria-label="RePlay"
            style={{}}
            onClick={() => {
              // emptyVideoChooseがtrueの場合は再生しない
              if (
                videoChoose.displaySettings.launcherSettings
                  .emptyVideoChoose === "true"
              ) {
                return;
              }
              videoRef.current?.play();

              // リプレイイベントを親ページに送信
              window.parent.postMessage(
                {
                  action: "vc_video_replay",
                  videoId: video?.id,
                  videoChooseId: videoChooseId,
                  domain: domain,
                  currentTime: videoRef.current?.currentTime || 0,
                },
                "*",
              );

              if (video && autoCloseVideos.includes(video.id)) {
                handleScrollToBottom();
              }
              setProgress(0);
              setIsVideoEnd(false);
            }}
          >
            <Iconify
              icon={"mdi:replay"}
              width={50}
              color={"rgba(255,255,255, 1)"}
            />
          </Button>
        </Box>
      )}

      {videoLoading && (
        <Box sx={commonStyles.videoLoading}>
          <CircularProgress size={32} style={{ color: "rgba(0, 0, 0, 0.4)" }} />
        </Box>
      )}

      {/*動画 */}
      <Box>
        {/* 中央のシングルタップエリア（再生/一時停止） */}
        <Box
          sx={{
            position: "absolute",
            left: 0,
            top: 0,
            width: "100%",
            height: "100%",
            zIndex: 210,
            cursor: "pointer",
          }}
          {...swipeHandlers(false)}
          onClick={(e) => {
            e.stopPropagation();
            if (videoRef.current) {
              if (!videoRef.current.paused) {
                videoRef.current.pause();
                setPlayButtonOpacity(1);
                handleScrollToTop();

                // 停止イベントを親ページに送信
                window.parent.postMessage(
                  {
                    action: "vc_video_pause",
                    videoId: video?.id,
                    videoChooseId: videoChooseId,
                    domain: domain,
                    currentTime: videoRef.current?.currentTime || 0,
                  },
                  "*",
                );
              } else {
                // emptyVideoChooseがtrueの場合は再生しない
                if (
                  videoChoose.displaySettings.launcherSettings
                    .emptyVideoChoose === "true"
                ) {
                  return;
                }
                videoRef.current.play();

                // 再生イベントを親ページに送信
                window.parent.postMessage(
                  {
                    action: "vc_video_play",
                    videoId: video?.id,
                    videoChooseId: videoChooseId,
                    domain: domain,
                    currentTime: videoRef.current?.currentTime || 0,
                  },
                  "*",
                );

                fadeOutButton();
                setPlayButtonClass("");
              }
            }
          }}
        />

        <video
          key={video?.video_url}
          playsInline
          ref={videoRef}
          preload="auto"
          poster={video?.thumbnail}
          style={{
            // height: "100vh",
            position: "absolute",
            left: "50%",
            transform: "translateX(-50%)",
            width: "100%",
            height: "100%",
            zIndex: 180,
            objectFit: mdUp ? "contain" : "cover",
            objectPosition: "center center",
          }}
          muted={isMuted}
        >
          <source src={video?.video_url} type="video/mp4" />
        </video>

        {/*nextActionの選択肢の動画を読み込む*/}
        {/* {video?.nextActions.map((nextActionKey: string) => {
          const next = videoChoose.nextActions[nextActionKey];
          const videoId = next?.video_id;
          const video = videoId ? videoChoose.videos[videoId] : undefined;

          return video?.video_url ? (
            <video
              key={nextActionKey}
              src={video.video_url}
              autoPlay
              loop
              muted
              style={{
                position: "absolute",
                left: "50%",
                transform: "translateX(-50%)",
                width: "100%",
                height: "100%",
                zIndex: 180,
                objectFit: mdUp ? "contain" : "cover",
                objectPosition: "center center",
                opacity: 0,
              }}
            >
              <source src={video?.video_url} type="video/mp4" />
            </video>
          ) : null;
        })} */}

        <img
          key={`thumbnail`}
          style={{
            width: "100%",
            height: "100%",
            position: "absolute",
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 200,
            opacity: opacity,
            objectFit: mdUp ? "contain" : "cover",
          }}
          src={video?.thumbnail}
        ></img>

        <Modal
          open={formModal}
          onClose={handleFormModalClose}
          closeAfterTransition
          disableScrollLock={true}
        >
          <Fade in={formModal} timeout={100}>
            <div
              style={{
                position: "absolute",
                left: "50%",
                transform: "translateX(-50%)",
                width: "100%",
                height: "100%",
                zIndex: 180,
                objectFit: "contain",
                objectPosition: "center center",
              }}
            >
              {confirmation ? (
                <Button
                  onClick={handleChangeConfirmation}
                  style={{
                    position: "absolute",
                    top: 10,
                    left: 10,
                    zIndex: 1201,
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    borderRadius: "100px",
                    padding: 0,
                    width: 40,
                    height: 40,
                    minWidth: 40,
                    minHeight: 40,
                    backgroundColor: "rgba(0, 0, 0, 0.7)",
                    transform: "none",
                  }}
                >
                  <Iconify
                    icon={"icon-park-outline:left"}
                    width={24}
                    color={"rgba(255,255,255, 1)"}
                  />
                </Button>
              ) : (
                <Button
                  onClick={() => {
                    handleFormModalClose();
                    if (isIFrame) {
                      window.parent.postMessage(
                        {
                          action: "vc_form_close",
                          videoChooseId: videoChooseId,
                        },
                        "*",
                      );
                    }
                  }}
                  style={{
                    position: "absolute",
                    top: 10,
                    right: 10,
                    zIndex: 1200,
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    borderRadius: "100px",
                    padding: 0,
                    width: 40,
                    height: 40,
                    minWidth: 40,
                    minHeight: 40,
                    backgroundColor: "rgba(0, 0, 0, 0.7)",
                    transform: "none",
                  }}
                >
                  <Iconify
                    icon={"mingcute:close-fill"}
                    width={24}
                    color={"rgba(255,255,255, 1)"}
                  />
                </Button>
              )}

              {videoChoose.formType === "slider" ? (
                <Swiper
                  spaceBetween={0}
                  slidesPerView={1}
                  style={{ height: "100vh" }}
                  allowTouchMove={false}
                  onSwiper={(swiper: SwiperCore) => {
                    swiperRef.current = swiper;
                  }}
                >
                  {videoChooseId &&
                    forms &&
                    videoChoose.formGroups &&
                    Object.values(videoChoose.formGroups)
                      .flatMap(
                        (group: any) =>
                          group.formOrder.map((order: any) => order.formId), // formOrder の formId を取得
                      )
                      .filter((formId) => forms[formId])
                      .map((formId) => (
                        <SwiperSlide>
                          <VideoChooseForm
                            key={formId}
                            videoChooseId={videoChooseId}
                            formId={formId}
                            form={forms[formId]}
                            fields={forms[formId]?.fields}
                            videoRef={videoRef}
                            formsOrdered={
                              forms[formId].form_group !== undefined
                                ? // @ts-ignore: Suppress TypeScript error for indexing with potentially undefined key
                                  videoChoose.formGroups[
                                    // @ts-ignore: Suppress TypeScript error for indexing with potentially undefined key
                                    forms[formId]?.form_group
                                  ]?.formOrder
                                : undefined
                            }
                            filledFormStep={filledFormStep}
                            handleFormStep={handleFormStep}
                            handleFormStepProgressBar={
                              handleFormStepProgressBar
                            }
                            selectedFormStep={selectedFormStep}
                            handleFormModalClose={handleFormModalClose}
                            handleVideoTypeNextAction={
                              handleVideoTypeNextAction
                            }
                            setSavedValues={setSavedValues}
                            savedValues={savedValues}
                            isIFrame={isIFrame}
                            confirmation={confirmation}
                            handleChangeConfirmation={handleChangeConfirmation}
                            handleSlideChange={handleSlideChange}
                            formType={"slider"}
                          />
                        </SwiperSlide>
                      ))}
                </Swiper>
              ) : (
                videoChooseId &&
                forms[selectedFormId] &&
                forms[selectedFormId].fields &&
                selectedFormId &&
                (forms[selectedFormId].form_group !== undefined ? (
                  <VideoChooseForm
                    videoChooseId={videoChooseId}
                    formId={selectedFormId}
                    form={forms[selectedFormId]}
                    fields={forms[selectedFormId].fields}
                    videoRef={videoRef}
                    formsOrdered={
                      // @ts-ignore: Suppress TypeScript error for indexing with potentially undefined key
                      videoChoose.formGroups[forms[selectedFormId].form_group]
                        .formOrder
                    }
                    filledFormStep={filledFormStep}
                    handleFormStep={handleFormStep}
                    handleFormStepProgressBar={handleFormStepProgressBar}
                    selectedFormStep={selectedFormStep}
                    handleFormModalClose={handleFormModalClose}
                    handleVideoTypeNextAction={handleVideoTypeNextAction}
                    setSavedValues={setSavedValues}
                    savedValues={savedValues}
                    isIFrame={isIFrame}
                    confirmation={confirmation}
                    handleChangeConfirmation={handleChangeConfirmation}
                    formType={"default"}
                  />
                ) : (
                  <VideoChooseForm
                    videoChooseId={videoChooseId}
                    formId={selectedFormId}
                    form={forms[selectedFormId]}
                    fields={forms[selectedFormId].fields}
                    videoRef={videoRef}
                    handleFormModalClose={handleFormModalClose}
                    handleVideoTypeNextAction={handleVideoTypeNextAction}
                    setSavedValues={setSavedValues}
                    savedValues={savedValues}
                    isIFrame={isIFrame}
                    confirmation={confirmation}
                    handleChangeConfirmation={handleChangeConfirmation}
                    formType={"default"}
                  />
                ))
              )}
            </div>
          </Fade>
        </Modal>
        <Modal
          open={customHtmlModal}
          onClose={handleCustomHtmlModalClose}
          closeAfterTransition
          sx={{
            display: "flex",
            alignItems: "stretch",
            justifyContent: "center",
            p: 0,
          }}
        >
          <Fade in={customHtmlModal} timeout={100}>
            <div
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                zIndex: 180,
                objectFit: "contain",
                objectPosition: "center center",
                padding: "0px",
                overflowY: "auto",
              }}
            >
              <Button
                onClick={handleCustomHtmlModalClose}
                style={{
                  position: "absolute",
                  top: 10,
                  right: 10,
                  zIndex: 1200,
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  borderRadius: "100px",
                  padding: 0,
                  width: 40,
                  height: 40,
                  minWidth: 40,
                  minHeight: 40,
                  backgroundColor: "rgba(0, 0, 0, 0.7)",
                  transform: "none",
                }}
              >
                <Iconify
                  icon={"mingcute:close-fill"}
                  width={24}
                  color={"rgba(255,255,255, 1)"}
                />
              </Button>

              <div
                style={{
                  width: "100%",
                  minHeight: "100vh",
                  flex: 1,
                  overflowY: "auto",
                  backgroundColor: "rgba(0, 0, 0, 0.5)",
                  padding: "0px",
                  boxSizing: "border-box",
                }}
                onClick={handleCustomHtmlContentClick}
              >
                {!customHtmlImageLoaded && (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      height: "100%",
                      width: "100%",
                      position: "relative",
                      overflow: "hidden",
                    }}
                  >
                    <div
                      style={{
                        position: "relative",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: "18px",
                        padding: "24px 32px",
                        textAlign: "center",
                      }}
                    >
                      <CircularProgress
                        style={{ color: "rgba(255,255,255,0.92)" }}
                        size={40}
                      />
                    </div>
                  </div>
                )}
                <div
                  style={{
                    width: "100%",
                    minHeight: "100vh",
                    visibility: customHtmlImageLoaded ? "visible" : "hidden",
                  }}
                  dangerouslySetInnerHTML={{
                    __html: selectedCustomHtml,
                  }}
                />
              </div>
            </div>
          </Fade>
        </Modal>
      </Box>
      {/* 閉じているときの字幕（bottom:120px 固定） */}
      {isShowButton && (
        <Subtitle
          subtitleUrl={video?.subtitle_url || null}
          subtitles={subtitles}
          currentTime={videoRef.current?.currentTime || 0}
          subtitleTextColor={
            video?.subtitle_text_color ? video?.subtitle_text_color : "#fff"
          }
          subtitleBackgroundColor={
            video?.subtitle_background_color
              ? video?.subtitle_background_color
              : "rgba(20, 20, 20, 0.5)"
          }
          subtitleFontSize={iframeXSdown ? 12 : video?.subtitle_font_size || 13}
          subtitleFontFamily={video?.subtitle_font_family || undefined}
          subtitleBold={video?.subtitle_bold || false}
        />
      )}
      {/* 選択肢トグルボタン */}
      {isShowButton && (
        <Button
          onClick={handleScrollToTop}
          className={"pulse-btn"}
          sx={[
            commonStyles.toggleOptionButton,
            {
              opacity: buttonOpacity,
              touchAction: "none",
            },
          ]}
        >
          <Iconify
            width={16}
            icon={"flowbite:angle-top-solid"}
            sx={{ color: "#fff" }}
          />
        </Button>
      )}

      {/* 選択肢ラッパー（字幕 + ボタン群） */}
      <Box
        sx={[
          commonStyles.optionsWrapper,
          {
            maxHeight: 390,
            transform: isShowButton ? "translateY(100%)" : "translateY(0)",
          },
        ]}
        {...swipeHandlers(true)}
        onClick={(e) => {
          // ボタン要素以外のクリック時は再生・停止ボタンの動作を実行
          if (!(e.target as HTMLElement).closest("button")) {
            if (!videoRef.current?.paused) {
              videoRef.current?.pause();
              setPlayButtonOpacity(1);
              handleScrollToTop();

              // 停止イベントを親ページに送信
              window.parent.postMessage(
                {
                  action: "vc_video_pause",
                  videoId: video?.id,
                  videoChooseId: videoChooseId,
                  domain: domain,
                  currentTime: videoRef.current?.currentTime || 0,
                },
                "*",
              );
            } else {
              // emptyVideoChooseがtrueの場合は再生しない
              if (
                videoChoose.displaySettings.launcherSettings
                  .emptyVideoChoose === "true"
              ) {
                return;
              }
              setAutoPlay(true);
              videoRef.current?.play();

              // 再生イベントを親ページに送信
              window.parent.postMessage(
                {
                  action: "vc_video_play",
                  videoId: video?.id,
                  videoChooseId: videoChooseId,
                  domain: domain,
                  currentTime: videoRef.current?.currentTime || 0,
                },
                "*",
              );

              if (
                process.env.REACT_APP_PROD &&
                videoChooseId &&
                video?.id &&
                !playLogRequest
              ) {
                setPlayLogRequest(true);
              }

              if (video && autoCloseVideos.includes(video.id)) {
                handleScrollToBottom();
              }
              fadeOutButton();
              setPlayButtonClass("");
            }
          }
        }}
      >
        {/* 開いているときの字幕（ボタン上に固定、スクロールしない） */}
        <Subtitle
          inline
          subtitleUrl={video?.subtitle_url || null}
          subtitles={subtitles}
          currentTime={videoRef.current?.currentTime || 0}
          subtitleTextColor={
            video?.subtitle_text_color ? video?.subtitle_text_color : "#fff"
          }
          subtitleBackgroundColor={
            video?.subtitle_background_color
              ? video?.subtitle_background_color
              : "rgba(20, 20, 20, 0.5)"
          }
          subtitleFontSize={iframeXSdown ? 12 : video?.subtitle_font_size || 13}
          subtitleFontFamily={video?.subtitle_font_family || undefined}
          subtitleBold={video?.subtitle_bold || false}
        />

        {/* ボタン群（ここだけスクロール） */}
        <Box
          ref={boxRef}
          data-vc-scroll-box
          onScroll={checkScrollable}
          onKeyDown={() => setHasScrolledOptions(true)}
          sx={[
            commonStyles.optionsScrollBox,
            { userSelect: "none", maxHeight: 325 },
          ]}
        >
          {video?.nextActions.map((nextAction, index) => {
            const currentAction = videoChoose.nextActions[nextAction];
            const nextActionId = video?.nextActions[index + 1];
            const nextActionData = nextActionId
              ? videoChoose.nextActions[nextActionId]
              : null;

            // 現在の要素が幅50%で左寄せ、次の要素が幅50%で右寄せの場合をチェック
            const currentWidth = (currentAction as any).option_width || "";
            const currentAlign = (currentAction as any).align || "";
            const nextWidth = nextActionData
              ? (nextActionData as any).option_width || ""
              : "";
            const nextAlign = nextActionData
              ? (nextActionData as any).align || ""
              : "";

            const shouldBeTwoColumn =
              currentWidth === "50%" &&
              currentAlign === "left" &&
              nextWidth === "50%" &&
              nextAlign === "right";

            // 前の要素が2列の左側だった場合はスキップ（右側として既にレンダリング済み）
            const prevActionId =
              index > 0 ? video?.nextActions[index - 1] : null;
            const prevActionData = prevActionId
              ? videoChoose.nextActions[prevActionId]
              : null;
            const prevWidth = prevActionData
              ? (prevActionData as any).option_width ||
                (prevActionData as any).width ||
                ""
              : "";
            const prevAlign = prevActionData
              ? (prevActionData as any).align ||
                (prevActionData as any).text_align ||
                ""
              : "";
            const isRightColumnOfPair =
              prevWidth === "50%" &&
              prevAlign === "left" &&
              currentWidth === "50%" &&
              currentAlign === "right";

            // 右側の要素として既にレンダリング済みの場合はスキップ
            if (isRightColumnOfPair) {
              return null;
            }

            const buttonElement = (
              <Button
                key={nextAction}
                sx={
                  videoChoose.nextActions[nextAction].button_display_type &&
                  videoChoose.nextActions[nextAction].button_display_type ===
                    "html"
                    ? [
                        {
                          width: "100%",
                          padding: 0,
                          marginBottom: "8px",
                          opacity:
                            videoChoose.displaySettings
                              .showClickedOptionOpacity &&
                            videoChoose.nextActions[nextAction].video_id &&
                            history.includes(
                              videoChoose.nextActions[nextAction].video_id!,
                            )
                              ? 0.5
                              : 1,
                        },
                      ]
                    : [
                        commonStyles.optionButton,
                        {
                          backgroundColor:
                            videoChoose.nextActions[nextAction].option_color !=
                              "" &&
                            videoChoose.nextActions[nextAction].option_color !=
                              null
                              ? videoChoose.nextActions[nextAction].option_color
                              : "rgba(5, 5, 5, 0.5)",
                          "&:hover": {
                            backgroundColor:
                              videoChoose.nextActions[nextAction]
                                .option_color != "" &&
                              videoChoose.nextActions[nextAction]
                                .option_color != null
                                ? videoChoose.nextActions[nextAction]
                                    .option_color
                                : "rgba(5, 5, 5, 0.5)",
                          },
                          padding: iframeXSdown ? "8px 0" : "10px 0",
                          width: shouldBeTwoColumn
                            ? iframeXSdown
                              ? "calc(50% - 4px)"
                              : "calc(50% - 4px)"
                            : iframeXSdown
                              ? "90%"
                              : "100%",
                          opacity:
                            videoChoose.displaySettings
                              .showClickedOptionOpacity &&
                            videoChoose.nextActions[nextAction].video_id &&
                            history.includes(
                              videoChoose.nextActions[nextAction].video_id!,
                            )
                              ? 0.5
                              : 1,
                        },
                      ]
                }
                onClick={(e) => {
                  e.stopPropagation();
                  window.parent.postMessage(
                    {
                      action: "vc_next_action",
                      consumerToken: consumerToken,
                      visitToken: visitToken,
                      nextActionId: nextAction,
                      domain: domain,
                      videoId: video?.id,
                      videoChooseId: videoChooseId,
                      currentTime: videoRef.current?.currentTime || 0,
                    },
                    "*",
                  );

                  if (videoChoose.nextActions[nextAction].type == "video") {
                    const nextVideoId = videoChoose.nextActions[nextAction];
                    if (
                      nextVideoId !== undefined &&
                      nextVideoId.video_id !== undefined
                    ) {
                      const nextVideo =
                        videoChoose.videos[nextVideoId.video_id];
                      if (nextVideo) {
                        setProgressTransition("none");
                        setAutoPlay(true);
                        setProgress(0);
                        setVideoLoading(true);
                        setOpacity(1);
                        setVideo(nextVideo);
                        setIsVideoEnd(false);
                        setPlayButtonOpacity(0);

                        if (nextVideoId.video_id !== undefined) {
                          const videoId: string = nextVideoId.video_id;
                          setHistory((prevHistory) => {
                            const currentIndex = video
                              ? prevHistory.indexOf(video.id)
                              : prevHistory.length - 1;
                            const truncated = prevHistory.slice(
                              0,
                              currentIndex + 1,
                            );
                            if (!truncated.includes(videoId)) {
                              return [...truncated, videoId];
                            }
                            return truncated;
                          });
                        }
                        if (videoChooseId) {
                          if (process.env.REACT_APP_PROD) {
                            // logRequest.nextAction({
                            //   consumerToken: consumerToken,
                            //   visitToken: visitToken,
                            //   videoChooseId: videoChooseId,
                            //   videoId: nextVideoId.video_id,
                            //   nextActionId: nextVideoId.id,
                            //   viewedAt: getJSTDate(),
                            // });
                          }
                        }
                      }
                    }
                  } else if (
                    videoChoose.nextActions[nextAction].type == "outer_link"
                  ) {
                    if (
                      videoChoose.nextActions[nextAction].measurement_tag !==
                        "" &&
                      videoChoose.nextActions[nextAction].measurement_tag !==
                        undefined &&
                      process.env.REACT_APP_PROD
                    ) {
                      window.parent.postMessage(
                        {
                          action: "setVcCvCookie",
                          videoChooseId: videoChooseId,
                          measurementTag:
                            videoChoose.nextActions[nextAction].measurement_tag,
                          videoId: video.id,
                          domain: domain,
                          getCtaByHref:
                            videoChoose.displaySettings.launcherSettings
                              .getCtaByHref,
                          getCtaByClass:
                            videoChoose.displaySettings.launcherSettings
                              .getCtaByClass,
                          getCtaById:
                            videoChoose.displaySettings.launcherSettings
                              .getCtaById,
                        },
                        "*",
                      );
                    }

                    if (videoChooseId) {
                      if (process.env.REACT_APP_PROD) {
                        // logRequest.nextAction({
                        //   consumerToken: consumerToken,
                        //   visitToken: visitToken,
                        //   videoChooseId: videoChooseId,
                        //   videoId: "no_video_id",
                        //   nextActionId: nextAction,
                        //   viewedAt: getJSTDate(),
                        // });
                      }
                    }
                    if (videoRef.current) {
                      videoRef.current.pause();
                      setPlayButtonOpacity(1);

                      // 停止イベントを親ページに送信
                      window.parent.postMessage(
                        {
                          action: "vc_video_pause",
                          videoId: video?.id,
                          videoChooseId: videoChooseId,
                          domain: domain,
                        },
                        "*",
                      );
                    }

                    if (isIFrame) {
                      if (videoChoose.nextActions[nextAction].open_new_window) {
                        window.open(
                          videoChoose.nextActions[nextAction].outer_link,
                          "_blank",
                        );
                      } else {
                        window.open(
                          videoChoose.nextActions[nextAction].outer_link,
                          "_self",
                        );
                      }
                    } else {
                      window.open(
                        videoChoose.nextActions[nextAction].outer_link,
                        "_blank",
                      );
                    }
                  } else if (
                    videoChoose.nextActions[nextAction].type == "internal_link"
                  ) {
                    if (videoRef.current) {
                      videoRef.current.pause();
                      setPlayButtonOpacity(1);

                      // 停止イベントを親ページに送信
                      window.parent.postMessage(
                        {
                          action: "vc_video_pause",
                          videoId: video?.id,
                          videoChooseId: videoChooseId,
                          domain: domain,
                        },
                        "*",
                      );
                    }

                    if (isIFrame) {
                      sendMessage(
                        videoChoose.nextActions[nextAction].anchor,
                        videoChoose.nextActions[nextAction]
                          .is_close_anchor_click,
                      );
                    } else {
                      if (videoChoose.nextActions[nextAction].other_link) {
                        window.open(
                          videoChoose.nextActions[nextAction].other_link,
                          "_blank",
                        );
                      }
                    }

                    if (videoChooseId) {
                      if (process.env.REACT_APP_PROD) {
                        // logRequest.nextAction({
                        //   consumerToken: consumerToken,
                        //   visitToken: visitToken,
                        //   videoChooseId: videoChooseId,
                        //   videoId: "no_video_id",
                        //   nextActionId: nextAction,
                        //   viewedAt: getJSTDate(),
                        // });
                      }
                    }
                  } else if (
                    videoChoose.nextActions[nextAction].type == "custom_form"
                  ) {
                    if (videoRef.current) {
                      videoRef.current.pause();
                      setPlayButtonOpacity(1);

                      // 停止イベントを親ページに送信
                      window.parent.postMessage(
                        {
                          action: "vc_video_pause",
                          videoId: video?.id,
                          videoChooseId: videoChooseId,
                          domain: domain,
                        },
                        "*",
                      );
                    }

                    const nextVideoId = videoChoose.nextActions[nextAction];
                    if (
                      nextVideoId !== undefined &&
                      nextVideoId.form_id !== undefined
                    ) {
                      setFormModal(true);
                      setSelectedFormId(nextVideoId.form_id);
                      if (forms[nextVideoId.form_id].form_group !== undefined) {
                        const formStep = videoChoose.formGroups[
                          // @ts-ignore: Suppress TypeScript error for indexing with potentially undefined key
                          forms[nextVideoId.form_id].form_group
                        ].formOrder.findIndex(
                          (form: any) => form.formId === nextVideoId.form_id,
                        );

                        setSelectedFormStep(formStep);
                        setFilledFormStep(formStep);
                      }
                    }
                    if (videoChooseId) {
                      if (process.env.REACT_APP_PROD) {
                        // logRequest.nextAction({
                        //   consumerToken: consumerToken,
                        //   visitToken: visitToken,
                        //   videoChooseId: videoChooseId,
                        //   videoId: "no_video_id",
                        //   nextActionId: nextAction,
                        //   viewedAt: getJSTDate(),
                        // });
                      }
                    }

                    if (
                      videoChoose.nextActions[nextAction].measurement_tag !==
                        "" &&
                      videoChoose.nextActions[nextAction].measurement_tag !==
                        undefined &&
                      process.env.REACT_APP_PROD
                    ) {
                      window.parent.postMessage(
                        {
                          action: "setVcCvCookie",
                          videoChooseId: videoChooseId,
                          measurementTag:
                            videoChoose.nextActions[nextAction].measurement_tag,
                          videoId: video.id,
                          domain: domain,
                          getCtaByHref:
                            videoChoose.displaySettings.launcherSettings
                              .getCtaByHref,
                          getCtaByClass:
                            videoChoose.displaySettings.launcherSettings
                              .getCtaByClass,
                          getCtaById:
                            videoChoose.displaySettings.launcherSettings
                              .getCtaById,
                        },
                        "*",
                      );
                    }
                  } else if (
                    videoChoose?.nextActions?.[nextAction]?.type === "phone"
                  ) {
                    if (videoRef.current) {
                      videoRef.current.pause();
                      setPlayButtonOpacity(1);

                      // 停止イベントを親ページに送信
                      window.parent.postMessage(
                        {
                          action: "vc_video_pause",
                          videoId: video?.id,
                          videoChooseId: videoChooseId,
                          domain: domain,
                        },
                        "*",
                      );
                    }
                    const action = videoChoose.nextActions[nextAction];

                    if (
                      action &&
                      action.phone_country_code !== undefined &&
                      action.phone_number !== undefined
                    ) {
                      const phone_number_with_country_code =
                        action.phone_country_code + action.phone_number;

                      const isPhoneSupported = (): boolean => {
                        const userAgent = navigator.userAgent;
                        return /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(
                          userAgent,
                        );
                      };

                      const makeCall = (phoneNumber: string): void => {
                        window.open(`tel:${phoneNumber}`);
                      };

                      const displayPhoneNumber = (
                        phoneNumber: string,
                      ): void => {
                        alert(
                          `このデバイスは直接電話をかけることができません。\nお手持ちの電話で以下の番号におかけください\n\n${phoneNumber}`,
                        );
                      };

                      const convertToDomesticPhoneNumber = (
                        phoneNumber: string,
                        countryCode: string,
                      ): string => {
                        const countryCodeMapping: {
                          [key: string]: (number: string) => string;
                        } = {
                          "+81": (number: string) => "0" + number.slice(3),
                          "+1": (number: string) => number.slice(2),
                        };

                        if (countryCodeMapping[countryCode]) {
                          return countryCodeMapping[countryCode](phoneNumber);
                        }

                        return phoneNumber; // Return the original phone number if no conversion logic is found
                      };

                      if (isPhoneSupported()) {
                        makeCall(phone_number_with_country_code);
                      } else {
                        const domesticPhoneNumber =
                          convertToDomesticPhoneNumber(
                            phone_number_with_country_code,
                            action.phone_country_code,
                          );
                        displayPhoneNumber(domesticPhoneNumber);
                      }
                    }
                  } else if (
                    videoChoose?.nextActions?.[nextAction]?.type ===
                    "custom_script"
                  ) {
                    if (videoRef.current) {
                      videoRef.current.pause();
                      setPlayButtonOpacity(1);

                      // 停止イベントを親ページに送信
                      window.parent.postMessage(
                        {
                          action: "vc_video_pause",
                          videoId: video?.id,
                          videoChooseId: videoChooseId,
                          domain: domain,
                        },
                        "*",
                      );
                    }

                    if (isIFrame) {
                      sendCostomScriptIdMessage(
                        videoChoose.nextActions[nextAction].custom_script_id,
                        videoChooseId,
                      );
                    } else {
                      if (videoChoose.nextActions[nextAction].other_link) {
                        window.open(
                          videoChoose.nextActions[nextAction].other_link,
                          "_blank",
                        );
                      }
                    }

                    if (videoChooseId) {
                      if (process.env.REACT_APP_PROD) {
                        // logRequest.nextAction({
                        //   consumerToken: consumerToken,
                        //   visitToken: visitToken,
                        //   videoChooseId: videoChooseId,
                        //   videoId: "no_video_id",
                        //   nextActionId: nextAction,
                        //   viewedAt: getJSTDate(),
                        // });
                      }
                    }
                    if (
                      videoChoose.nextActions[nextAction].measurement_tag !==
                        "" &&
                      videoChoose.nextActions[nextAction].measurement_tag !==
                        undefined &&
                      process.env.REACT_APP_PROD
                    ) {
                      window.parent.postMessage(
                        {
                          action: "setVcCvCookie",
                          videoChooseId: videoChooseId,
                          measurementTag:
                            videoChoose.nextActions[nextAction].measurement_tag,
                          videoId: video.id,
                          domain: domain,
                          getCtaByHref:
                            videoChoose.displaySettings.launcherSettings
                              .getCtaByHref,
                          getCtaByClass:
                            videoChoose.displaySettings.launcherSettings
                              .getCtaByClass,
                          getCtaById:
                            videoChoose.displaySettings.launcherSettings
                              .getCtaById,
                        },
                        "*",
                      );
                    }
                  } else if (
                    videoChoose?.nextActions?.[nextAction]?.type ===
                    "custom_html"
                  ) {
                    if (videoRef.current) {
                      videoRef.current.pause();
                      setPlayButtonOpacity(1);
                    }

                    const action = videoChoose?.nextActions?.[nextAction];

                    setSelectedCustomHtmlActionId(action?.id || nextAction);
                    setCustomHtmlModal(true);

                    if (videoChooseId) {
                      if (process.env.REACT_APP_PROD) {
                        // logRequest.nextAction({
                        //   consumerToken: consumerToken,
                        //   visitToken: visitToken,
                        //   videoChooseId: videoChooseId,
                        //   videoId: "no_video_id",
                        //   nextActionId: nextAction,
                        //   viewedAt: getJSTDate(),
                        // });
                      }
                    }
                    if (
                      videoChoose.nextActions[nextAction].measurement_tag !==
                        "" &&
                      videoChoose.nextActions[nextAction].measurement_tag !==
                        undefined &&
                      process.env.REACT_APP_PROD
                    ) {
                      window.parent.postMessage(
                        {
                          action: "setVcCvCookie",
                          videoChooseId: videoChooseId,
                          measurementTag:
                            videoChoose.nextActions[nextAction].measurement_tag,
                          videoId: video.id,
                          domain: domain,
                          getCtaByHref:
                            videoChoose.displaySettings.launcherSettings
                              .getCtaByHref,
                          getCtaByClass:
                            videoChoose.displaySettings.launcherSettings
                              .getCtaByClass,
                          getCtaById:
                            videoChoose.displaySettings.launcherSettings
                              .getCtaById,
                        },
                        "*",
                      );
                    }
                  }
                }}
              >
                {videoChoose.nextActions[nextAction].button_display_type &&
                videoChoose.nextActions[nextAction].button_display_type ===
                  "html" &&
                videoChoose.nextActions[nextAction].button_html !==
                  undefined ? (
                  <div
                    style={{ width: "100%", textTransform: "none" }}
                    dangerouslySetInnerHTML={{
                      __html: videoChoose.nextActions[nextAction].button_html!,
                    }}
                  />
                ) : (
                  <span
                    style={{
                      color: videoChoose.nextActions[nextAction]
                        .option_text_color
                        ? videoChoose.nextActions[nextAction].option_text_color
                        : "#fff",
                      textTransform: "none",
                      fontSize: iframeXSdown
                        ? 14
                        : videoChoose.nextActions[nextAction]
                            .option_font_size || 16,
                      fontWeight: videoChoose.nextActions[nextAction]
                        .option_bold
                        ? 700
                        : 400,
                    }}
                  >
                    {videoChoose.nextActions[nextAction].option_text}
                  </span>
                )}

                {/* <Iconify
                style={{ position: "absolute", right: 12 }}
                icon={"mingcute:right-line"}
                width={22}
                color={"#ddd"}
              /> */}
              </Button>
            );

            // 2列の場合、右側のボタンも追加
            if (shouldBeTwoColumn && nextActionData) {
              // 右側のボタンのonClick処理（左側と同じロジック）
              const rightButtonOnClick = (e: React.MouseEvent) => {
                e.stopPropagation();
                window.parent.postMessage(
                  {
                    action: "vc_next_action",
                    consumerToken: consumerToken,
                    visitToken: visitToken,
                    nextActionId: nextActionId,
                    domain: domain,
                    videoId: video?.id,
                    videoChooseId: videoChooseId,
                    currentTime: videoRef.current?.currentTime || 0,
                  },
                  "*",
                );

                if (nextActionData.type == "video") {
                  const nextVideoId = nextActionData;
                  if (
                    nextVideoId !== undefined &&
                    nextVideoId.video_id !== undefined
                  ) {
                    const nextVideo = videoChoose.videos[nextVideoId.video_id];
                    if (nextVideo) {
                      setProgressTransition("none");
                      setAutoPlay(true);
                      setProgress(0);
                      setVideoLoading(true);
                      setOpacity(1);
                      setVideo(nextVideo);
                      setIsVideoEnd(false);
                      setPlayButtonOpacity(0);

                      if (nextVideoId.video_id !== undefined) {
                        const videoId: string = nextVideoId.video_id;
                        setHistory((prevHistory) => {
                          const currentIndex = video
                            ? prevHistory.indexOf(video.id)
                            : prevHistory.length - 1;
                          const truncated = prevHistory.slice(
                            0,
                            currentIndex + 1,
                          );
                          if (!truncated.includes(videoId)) {
                            return [...truncated, videoId];
                          }
                          return truncated;
                        });
                      }
                      if (videoChooseId) {
                        if (process.env.REACT_APP_PROD) {
                          // logRequest.nextAction({
                          //   consumerToken: consumerToken,
                          //   visitToken: visitToken,
                          //   videoChooseId: videoChooseId,
                          //   videoId: nextVideoId.video_id,
                          //   nextActionId: nextVideoId.id,
                          //   viewedAt: getJSTDate(),
                          // });
                        }
                      }
                    }
                  }
                } else if (nextActionData.type == "outer_link") {
                  if (
                    nextActionData.measurement_tag !== "" &&
                    nextActionData.measurement_tag !== undefined &&
                    process.env.REACT_APP_PROD
                  ) {
                    window.parent.postMessage(
                      {
                        action: "setVcCvCookie",
                        videoChooseId: videoChooseId,
                        measurementTag: nextActionData.measurement_tag,
                        videoId: video.id,
                        domain: domain,
                        getCtaByHref:
                          videoChoose.displaySettings.launcherSettings
                            .getCtaByHref,
                        getCtaByClass:
                          videoChoose.displaySettings.launcherSettings
                            .getCtaByClass,
                        getCtaById:
                          videoChoose.displaySettings.launcherSettings
                            .getCtaById,
                      },
                      "*",
                    );
                  }

                  if (videoChooseId) {
                    if (process.env.REACT_APP_PROD) {
                      // logRequest.nextAction({
                      //   consumerToken: consumerToken,
                      //   visitToken: visitToken,
                      //   videoChooseId: videoChooseId,
                      //   videoId: "no_video_id",
                      //   nextActionId: nextActionId,
                      //   viewedAt: getJSTDate(),
                      // });
                    }
                  }
                  if (videoRef.current) {
                    videoRef.current.pause();
                    setPlayButtonOpacity(1);

                    // 停止イベントを親ページに送信
                    window.parent.postMessage(
                      {
                        action: "vc_video_pause",
                        videoId: video?.id,
                        videoChooseId: videoChooseId,
                        domain: domain,
                      },
                      "*",
                    );
                  }

                  if (isIFrame) {
                    if (nextActionData.open_new_window) {
                      window.open(nextActionData.outer_link, "_blank");
                    } else {
                      window.open(nextActionData.outer_link, "_self");
                    }
                  } else {
                    window.open(nextActionData.outer_link, "_blank");
                  }
                } else if (nextActionData.type == "internal_link") {
                  if (videoRef.current) {
                    videoRef.current.pause();
                    setPlayButtonOpacity(1);

                    // 停止イベントを親ページに送信
                    window.parent.postMessage(
                      {
                        action: "vc_video_pause",
                        videoId: video?.id,
                        videoChooseId: videoChooseId,
                        domain: domain,
                      },
                      "*",
                    );
                  }

                  if (isIFrame) {
                    sendMessage(
                      nextActionData.anchor,
                      nextActionData.is_close_anchor_click,
                    );
                  } else {
                    if (nextActionData.other_link) {
                      window.open(nextActionData.other_link, "_blank");
                    }
                  }

                  if (videoChooseId) {
                    if (process.env.REACT_APP_PROD) {
                      // logRequest.nextAction({
                      //   consumerToken: consumerToken,
                      //   visitToken: visitToken,
                      //   videoChooseId: videoChooseId,
                      //   videoId: "no_video_id",
                      //   nextActionId: nextActionId,
                      //   viewedAt: getJSTDate(),
                      // });
                    }
                  }
                } else if (nextActionData.type == "custom_form") {
                  if (videoRef.current) {
                    videoRef.current.pause();
                    setPlayButtonOpacity(1);

                    // 停止イベントを親ページに送信
                    window.parent.postMessage(
                      {
                        action: "vc_video_pause",
                        videoId: video?.id,
                        videoChooseId: videoChooseId,
                        domain: domain,
                      },
                      "*",
                    );
                  }

                  const nextVideoId = nextActionData;
                  if (
                    nextVideoId !== undefined &&
                    nextVideoId.form_id !== undefined
                  ) {
                    setFormModal(true);
                    setSelectedFormId(nextVideoId.form_id);
                    if (forms[nextVideoId.form_id].form_group !== undefined) {
                      const formStep = videoChoose.formGroups[
                        // @ts-ignore: Suppress TypeScript error for indexing with potentially undefined key
                        forms[nextVideoId.form_id].form_group
                      ].formOrder.findIndex(
                        (form: any) => form.formId === nextVideoId.form_id,
                      );

                      setSelectedFormStep(formStep);
                      setFilledFormStep(formStep);
                    }
                  }
                  if (videoChooseId) {
                    if (process.env.REACT_APP_PROD) {
                      // logRequest.nextAction({
                      //   consumerToken: consumerToken,
                      //   visitToken: visitToken,
                      //   videoChooseId: videoChooseId,
                      //   videoId: "no_video_id",
                      //   nextActionId: nextActionId,
                      //   viewedAt: getJSTDate(),
                      // });
                    }
                  }

                  if (
                    nextActionData.measurement_tag !== "" &&
                    nextActionData.measurement_tag !== undefined &&
                    process.env.REACT_APP_PROD
                  ) {
                    window.parent.postMessage(
                      {
                        action: "setVcCvCookie",
                        videoChooseId: videoChooseId,
                        measurementTag: nextActionData.measurement_tag,
                        videoId: video.id,
                        domain: domain,
                        getCtaByHref:
                          videoChoose.displaySettings.launcherSettings
                            .getCtaByHref,
                        getCtaByClass:
                          videoChoose.displaySettings.launcherSettings
                            .getCtaByClass,
                        getCtaById:
                          videoChoose.displaySettings.launcherSettings
                            .getCtaById,
                      },
                      "*",
                    );
                  }
                } else if (nextActionData?.type === "phone") {
                  if (videoRef.current) {
                    videoRef.current.pause();
                    setPlayButtonOpacity(1);

                    // 停止イベントを親ページに送信
                    window.parent.postMessage(
                      {
                        action: "vc_video_pause",
                        videoId: video?.id,
                        videoChooseId: videoChooseId,
                        domain: domain,
                      },
                      "*",
                    );
                  }
                  const action = nextActionData;

                  if (
                    action &&
                    action.phone_country_code !== undefined &&
                    action.phone_number !== undefined
                  ) {
                    const phone_number_with_country_code =
                      action.phone_country_code + action.phone_number;

                    const isPhoneSupported = (): boolean => {
                      const userAgent = navigator.userAgent;
                      return /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(
                        userAgent,
                      );
                    };

                    const makeCall = (phoneNumber: string): void => {
                      window.open(`tel:${phoneNumber}`);
                    };

                    const displayPhoneNumber = (phoneNumber: string): void => {
                      alert(
                        `このデバイスは直接電話をかけることができません。\nお手持ちの電話で以下の番号におかけください\n\n${phoneNumber}`,
                      );
                    };

                    const convertToDomesticPhoneNumber = (
                      phoneNumber: string,
                      countryCode: string,
                    ): string => {
                      const countryCodeMapping: {
                        [key: string]: (number: string) => string;
                      } = {
                        "+81": (number: string) => "0" + number.slice(3),
                        "+1": (number: string) => number.slice(2),
                      };

                      if (countryCodeMapping[countryCode]) {
                        return countryCodeMapping[countryCode](phoneNumber);
                      }

                      return phoneNumber;
                    };

                    if (isPhoneSupported()) {
                      makeCall(phone_number_with_country_code);
                    } else {
                      const domesticPhoneNumber = convertToDomesticPhoneNumber(
                        phone_number_with_country_code,
                        action.phone_country_code,
                      );
                      displayPhoneNumber(domesticPhoneNumber);
                    }
                  }
                } else if (nextActionData?.type === "custom_script") {
                  if (videoRef.current) {
                    videoRef.current.pause();
                    setPlayButtonOpacity(1);

                    // 停止イベントを親ページに送信
                    window.parent.postMessage(
                      {
                        action: "vc_video_pause",
                        videoId: video?.id,
                        videoChooseId: videoChooseId,
                        domain: domain,
                      },
                      "*",
                    );
                  }

                  if (isIFrame) {
                    sendCostomScriptIdMessage(
                      nextActionData.custom_script_id,
                      videoChooseId,
                    );
                  } else {
                    if (nextActionData.other_link) {
                      window.open(nextActionData.other_link, "_blank");
                    }
                  }

                  if (videoChooseId) {
                    if (process.env.REACT_APP_PROD) {
                      // logRequest.nextAction({
                      //   consumerToken: consumerToken,
                      //   visitToken: visitToken,
                      //   videoChooseId: videoChooseId,
                      //   videoId: "no_video_id",
                      //   nextActionId: nextActionId,
                      //   viewedAt: getJSTDate(),
                      // });
                    }
                  }
                  if (
                    nextActionData.measurement_tag !== "" &&
                    nextActionData.measurement_tag !== undefined &&
                    process.env.REACT_APP_PROD
                  ) {
                    window.parent.postMessage(
                      {
                        action: "setVcCvCookie",
                        videoChooseId: videoChooseId,
                        measurementTag: nextActionData.measurement_tag,
                        videoId: video.id,
                        domain: domain,
                        getCtaByHref:
                          videoChoose.displaySettings.launcherSettings
                            .getCtaByHref,
                        getCtaByClass:
                          videoChoose.displaySettings.launcherSettings
                            .getCtaByClass,
                        getCtaById:
                          videoChoose.displaySettings.launcherSettings
                            .getCtaById,
                      },
                      "*",
                    );
                  }
                } else if (nextActionData?.type === "custom_html") {
                  if (videoRef.current) {
                    videoRef.current.pause();
                    setPlayButtonOpacity(1);
                  }

                  const action = nextActionData;

                  setSelectedCustomHtmlActionId(action?.id || nextActionId);
                  setCustomHtmlModal(true);

                  if (videoChooseId) {
                    if (process.env.REACT_APP_PROD) {
                      // logRequest.nextAction({
                      //   consumerToken: consumerToken,
                      //   visitToken: visitToken,
                      //   videoChooseId: videoChooseId,
                      //   videoId: "no_video_id",
                      //   nextActionId: nextActionId,
                      //   viewedAt: getJSTDate(),
                      // });
                    }
                  }
                  if (
                    nextActionData.measurement_tag !== "" &&
                    nextActionData.measurement_tag !== undefined &&
                    process.env.REACT_APP_PROD
                  ) {
                    window.parent.postMessage(
                      {
                        action: "setVcCvCookie",
                        videoChooseId: videoChooseId,
                        measurementTag: nextActionData.measurement_tag,
                        videoId: video.id,
                        domain: domain,
                        getCtaByHref:
                          videoChoose.displaySettings.launcherSettings
                            .getCtaByHref,
                        getCtaByClass:
                          videoChoose.displaySettings.launcherSettings
                            .getCtaByClass,
                        getCtaById:
                          videoChoose.displaySettings.launcherSettings
                            .getCtaById,
                      },
                      "*",
                    );
                  }
                }
              };

              const rightButtonElement = (
                <Button
                  key={nextActionId}
                  sx={
                    nextActionData.button_display_type &&
                    nextActionData.button_display_type === "html"
                      ? [
                          {
                            width: "100%",
                            padding: 0,
                            marginBottom: "8px",
                            opacity:
                              videoChoose.displaySettings
                                .showClickedOptionOpacity &&
                              nextActionData.video_id &&
                              history.includes(nextActionData.video_id)
                                ? 0.5
                                : 1,
                          },
                        ]
                      : [
                          commonStyles.optionButton,
                          {
                            backgroundColor:
                              nextActionData.option_color != "" &&
                              nextActionData.option_color != null
                                ? nextActionData.option_color
                                : "rgba(5, 5, 5, 0.5)",
                            "&:hover": {
                              backgroundColor:
                                nextActionData.option_color != "" &&
                                nextActionData.option_color != null
                                  ? nextActionData.option_color
                                  : "rgba(5, 5, 5, 0.5)",
                            },
                            padding: iframeXSdown ? "8px 0" : "10px 0",
                            width: iframeXSdown
                              ? "calc(50% - 4px)"
                              : "calc(50% - 4px)",
                            opacity:
                              videoChoose.displaySettings
                                .showClickedOptionOpacity &&
                              nextActionData.video_id &&
                              history.includes(nextActionData.video_id)
                                ? 0.5
                                : 1,
                          },
                        ]
                  }
                  onClick={rightButtonOnClick}
                >
                  {nextActionData.button_display_type &&
                  nextActionData.button_display_type === "html" &&
                  nextActionData.button_html !== undefined ? (
                    <div
                      style={{ width: "100%", textTransform: "none" }}
                      dangerouslySetInnerHTML={{
                        __html: nextActionData.button_html!,
                      }}
                    />
                  ) : (
                    <span
                      style={{
                        color: nextActionData.option_text_color
                          ? nextActionData.option_text_color
                          : "#fff",
                        textTransform: "none",
                        fontSize: iframeXSdown
                          ? 14
                          : nextActionData.option_font_size || 16,
                        fontWeight: nextActionData.option_bold ? 700 : 400,
                      }}
                    >
                      {nextActionData.option_text}
                    </span>
                  )}
                </Button>
              );

              return (
                <Stack
                  key={`pair-${nextAction}`}
                  direction="row"
                  spacing={1}
                  sx={{
                    width: "100%",
                    gap: 0,
                    justifyContent: "space-between",
                    marginBottom: "8px",
                  }}
                >
                  {buttonElement}
                  {rightButtonElement}
                </Stack>
              );
            }

            return buttonElement;
          })}

          {hideCopyright ? (
            <Box
              data-vc-options-footer="true"
              sx={[
                commonStyles.footerBox,
                { padding: iframeXSdown ? "0.2rem 0" : "0.6rem 0" },
              ]}
            ></Box>
          ) : (
            <Box
              data-vc-options-footer="true"
              sx={[
                commonStyles.footerBox,
                { padding: iframeXSdown ? "0.2rem 0" : "0.6rem 0" },
              ]}
            >
              <Box
                style={{
                  fontSize: 12,
                  fontWeight: 100,
                  letterSpacing: "-0.2px",
                }}
              >
                Powered by &nbsp;
              </Box>
              <Button
                onClick={() => {
                  if (videoRef.current) {
                    videoRef.current.pause();
                    setPlayButtonOpacity(1);
                  }
                  window.open(
                    `${process.env.REACT_APP_CONSUMER_URL}`,
                    "_blank",
                  );
                }}
                sx={{
                  height: "1px",
                  display: "flex",
                  alignItems: "center",
                  padding: 0,
                }}
              >
                <img
                  src={"/assets/footer-logo.png"}
                  style={{ width: 95, borderRadius: 1.5 }}
                />
              </Button>
            </Box>
          )}
        </Box>
        {hasMoreContent && !hasScrolledOptions && (
          <div
            key={video?.id}
            className="scroll-bounce-indicator"
            aria-hidden="true"
          >
            <TouchAppIcon className="scroll-hint-touch" sx={{ fontSize: 32 }} />
          </div>
        )}
      </Box>

      {iframeLoading && (
        <Box
          sx={{
            width: "100%",
            height: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            position: "absolute",
            top: 0,
            left: 0,
            zIndex: 99999,
            backgroundColor: "rgba(255, 255, 255, 1)",
          }}
        >
          <CircularProgress size={32} style={{}} />
        </Box>
      )}
    </div>
  );
}

const commonStyles = {
  gradientContainer: {
    background: "rgba(0,0,0,.75)",
    display: "flex",
    justifyContent: "center",
    // height: "100vh",
    height: "-webkit-fill-available",
    position: "fixed" as "fixed",
    top: 0,
    width: "100%",
    zIndex: 300,
    right: 0,
    bottom: 0,
    left: 0,
  },
  progressBarBox: {
    position: "absolute",
    top: 20,
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    zIndex: 500,
  },
  titleBox: {
    position: "absolute",
    zIndex: 500,
    top: 30,
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
  },
  titleTypography: {
    fontWeight: 100,
    fontSize: 11,
    textAlign: "left",
  },
  backButton: {
    position: "absolute",
    zIndex: 500,
    display: "flex",
    justifyContent: "flex-start",
    alignItems: "center",
    padding: 0,
  },
  speakerButtonBox: {
    position: "absolute",
    top: "calc(37% - 2.5rem)",
    zIndex: 500,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },
  speakerButton: {
    borderRadius: 54,
    width: 80,
    height: 80,
    backgroundColor: "rgba(0,0,0, 0.5)",
    justifyContent: "center",
    alignItems: "center",
    transform: "none",
  },
  centerButton: {
    position: "absolute",
    top: "calc(37% - 2.5rem)",
    zIndex: 500,
    borderRadius: 54,
    width: 80,
    height: 80,
    backgroundColor: "rgba(0,0,0, 0.5)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    transform: "none",
  },
  videoLoading: {
    position: "absolute",
    top: "calc(37% - 2.5rem)",
    zIndex: 500,
    borderRadius: 54,
    width: 80,
    height: 80,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    transform: "none",
  },
  toggleOptionButton: {
    position: "absolute",
    transition: "bottom 0.5s ease-in-out",
    bottom: 30,
    zIndex: 500,
    minWidth: "auto",
    borderRadius: "50%",
  },
  optionsWrapper: {
    position: "absolute",
    bottom: 0,
    display: "flex",
    alignItems: "center",
    flexDirection: "column",
    width: 570,
    paddingLeft: "124px",
    paddingRight: "124px",
    zIndex: 300,
    overflow: "hidden",
    transition: "transform 0.4s ease-in-out",
    "& .scroll-bounce-indicator": {
      position: "absolute",
      top: "50%",
      right: "100px",
      transform: "translateY(-50%)",
      zIndex: 1,
    },
  },
  optionsScrollBox: {
    width: "100%",
    flex: "1 1 auto",
    minHeight: 0,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    overflowY: "auto",
    "&::-webkit-scrollbar": {
      display: "none",
    },
    msOverflowStyle: "none",
    scrollbarWidth: "none",
    "& > *": {
      flexShrink: 0,
    },
  },
  optionButton: {
    position: "relative",
    cursor: "pointer",
    alignItems: "center",
    justifyContent: "center",
    transition: "all .15s 0s cubic-bezier(.25,.46,.45,.94)",
    willChange: "all",
    borderRadius: "2rem",
    height: "3.2rem",
    // padding: "1rem 0",
    fontWeight: 500,
    color: "#fff",
    textDecoration: "none",
    display: "inline-flex",
    backgroundColor: "rgba(5, 5, 5, 0.5)",
    marginBottom: "8px",
    fontSize: 16,
    "&:hover": {
      backgroundColor: "rgba(5, 5, 5, 0.5)",
    },
  },
  footerBox: {
    position: "relative",
    cursor: "pointer",
    alignItems: "center",
    justifyContent: "center",
    transition: "all .15s 0s cubic-bezier(.25,.46,.45,.94)",
    borderRadius: "2rem",
    height: "2.6rem",
    fontWeight: 500,
    color: "#fff",
    textDecoration: "none",
    display: "inline-flex",
    width: "100%",
    marginTop: "6px",
    marginBottom: "2px",
    opacity: 0.7,
  },
};
